// Cairo, "Ramadan night by the pyramids": a mild late-February night in Ramadan from a rooftop café in Nazlet
// el-Samman, the village at the foot of the Giza plateau, looking west-northwest over its rooftops at the pyramids
// during the sound-and-light show. From this spot, a few hundred metres east of the Sphinx, the Sphinx faces us
// (its south flank showing a little on the left), Khafre's pyramid rises straight behind it with the last of its
// limestone casing round the tip, Khufu's Great Pyramid stands to the right, seen corner-on and nearer (so it looks
// the tallest from here), with its flat top, the survey mast and its three ruined queens' pyramids in front of its
// east face, and Menkaure's small pyramid sits far to the left with its three queens beyond it. The show floods them
// gold, then sweeps new colours across the plateau every half minute (blue, violet, green, red), and once a loop the
// pyramids go dark while a spotlight picks out the Sphinx's face as it "speaks". Stars over the desert, a young
// Ramadan crescent low in the west-southwest, thin cloud.
// In between: the village's unfinished brick-and-concrete rooftops with rebar, satellite dishes all aimed the same
// way, water tanks and laundry, a mosque minaret lit green between the two great pyramids, a pigeon loft whose flock
// circles and comes home, a rooftop billboard for an invented konafa shop, the show's audience in its stands (phones
// flashing). Below us a lane strung with zeena (paper-pennant garlands) and fanous lanterns: a lantern stall, a café
// with its TV, a ful cart steaming, a tuk-tuk, a horse carriage, camels and horses walking home to their stables,
// children with little lanterns. On our roof: a tent-fabric valance, a big hanging brass fanous, cushioned benches,
// a brass tray of tea glasses, a glowing shisha and a cat on the parapet watching the show. Scraps of zeena torn
// loose flutter down at three depths.
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, env, hash, rng, bayer, hex,
  makeCanvas, rect, makeHalo, drawHalo, FONT3, makePitch } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                             // the scene's 480x270 context, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= Scene constants ================= */
const BASE = 136;                                    // the pyramids' base line on the plateau
const KF = { x: 186, tip: 60, hw: 64 };              // Khafre: apex column, tip row, half-width at the base
const KU = { top: 50, x0: 375, x1: 379, l: 292, r: 482, ar: 364 };   // Khufu: flat top, SW corner, NE corner, SE arris foot
const MK = { x: 74, tip: 108, hw: 26 };              // Menkaure
const SX = 194, STOP = 114;                          // the Sphinx: centre column, the top of its head
const MIN = { x: 268, tip: 62 };                     // the minaret between Khafre and Khufu: centre, crescent tip
const MOON = { x: 136, y: 44, r: 7 };                // a young crescent low in the west-southwest, horns up
const BB = { x: 96, y: 82, w: 46, h: 20 };           // rooftop billboard face (exported as `billboard`)
const BB_ROOF = 138;                                 // the roof its scaffold stands on
const LANE = 199;                                    // the lane's ground line (wheels, hooves, feet)
const PAR = 204;                                     // our parapet's top ledge
const FLOOR = 216;                                   // our roof's floor starts here
const FAN = { x: 31, top: 60 };                      // the big hanging fanous: centre column, top of its cap
const DOVE = { x0: 256, x1: 281, top: 136, roof: 156 };   // the pigeon loft on the low roof right of the Sphinx
const CAT = { x: 358 };                              // the cat on the parapet
const SHISHA = { x: 300, top: 205 };                 // the shisha's coal bowl
const FUL = { x: 44 };                               // the ful cart in the lane

/* ================= Colour + pixel helpers ================= */
const toHex = (r, g, b) => '#' + ((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1);
const mixHex = (a, b, t) => { const A = hex(a), B = hex(b); return toHex(...A.map((v, i) => Math.round(lerp(v, B[i], t)))); };
const RGB = {};
const rgbOf = h => RGB[h] || (RGB[h] = hex(h));
/** ordered-dither pick from a palette (v in 0..1); soft < 1 narrows the dithered band between tones */
function dpick(pal, v, i, y, soft = 1) {
  const f = clamp(v, 0, 1) * (pal.length - 1);
  let k = Math.floor(f);
  if (f - k > 0.5 + (bayer(i, y) - 0.5) * soft) k++;
  return pal[Math.min(k, pal.length - 1)];
}
/** ImageData-backed painter for prerendering (hex colours). `own` tracks which object owns a pixel. */
function painter(w = W, h = H, track = false) {
  const [c, x] = makeCanvas(w, h);
  const img = x.createImageData(w, h), d = img.data;
  const own = track ? new Int32Array(w * h).fill(-1) : null;
  const p = { c, x, d, w, h, own, id: -1 };
  p.set = (i, y, col) => {
    i = Math.round(i); y = Math.round(y);
    if (i < 0 || y < 0 || i >= w || y >= h || !col) return;
    const k = rgbOf(col), o = (y * w + i) * 4;
    d[o] = k[0]; d[o + 1] = k[1]; d[o + 2] = k[2]; d[o + 3] = 255;
    if (own) own[y * w + i] = p.id;
  };
  p.has = (i, y) => i >= 0 && y >= 0 && i < w && y < h && d[(y * w + i) * 4 + 3] > 0;
  p.fill = (x0, y0, ww, hh, col) => { for (let y = y0; y < y0 + hh; y++) for (let i = x0; i < x0 + ww; i++) p.set(i, y, typeof col === 'function' ? col(i, y) : col); };
  p.done = () => { x.putImageData(img, 0, 0); return c; };
  return p;
}
/** sprite from rows of characters and a colour key ('.' or unknown = transparent) */
function sprite(rows, key, flip = false) {
  const h = rows.length, w = Math.max(...rows.map(r => r.length));
  const p = painter(w, h);
  rows.forEach((row, y) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) p.set(flip ? w - 1 - i : i, y, col); } });
  return p.done();
}
function flipCanvas(src) {
  const [c, x] = makeCanvas(src.width, src.height);
  x.translate(src.width, 0); x.scale(-1, 1); x.drawImage(src, 0, 0);
  return c;
}
function paintRows(p, rows, key, x0, y0) {
  rows.forEach((row, j) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) p.set(x0 + i, y0 + j, col); } });
}
/** FONT3 text straight into a painter, colour picked per glyph row */
function ptext(p, s, x, y, colAt) {
  let cx = x;
  for (const ch of s) {
    const g = FONT3[ch];
    if (g) g.forEach((row, j) => { for (let i = 0; i < 3; i++) if (row[i] === '1') p.set(cx + i, y + j, colAt(j)); });
    cx += 4;
  }
}
/** a straight 1-px line into a painter */
function pline(p, x0, y0, x1, y1, col) {
  const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0), 1);
  for (let k = 0; k <= n; k++) p.set(Math.round(lerp(x0, x1, k / n)), Math.round(lerp(y0, y1, k / n)), typeof col === 'function' ? col(k / n) : col);
}

/* ================= Sky: deep navy over the desert, a violet haze warmed by the plateau's lights ================= */
const SKY = ['#03041a', '#05061e', '#070923', '#0a0c28', '#0d102e', '#111334', '#15163a', '#1a1a40', '#201d45', '#27214a',
  '#2e254d', '#36294f', '#3f2d50', '#48324f'].map(hex);
function renderSky() {
  const [c, x] = makeCanvas(W, H);
  const img = x.createImageData(W, H), d = img.data;
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    let v = Math.pow(Math.min(1, y / 140), 1.7);
    v += Math.max(0, 1 - Math.hypot((i - 250) / 260, (y - 132) / 70)) * 0.16;    // the lit plateau in the haze
    v += Math.max(0, 1 - Math.hypot((i - 40) / 140, (y - 120) / 40)) * 0.05;     // the last of the dusk, west-southwest
    const f = clamp(v, 0, 1) * (SKY.length - 1);
    let k = Math.floor(f);
    if (f - k > bayer(i, y)) k++;
    const col = SKY[Math.min(k, SKY.length - 1)], o = (y * W + i) * 4;
    d[o] = col[0]; d[o + 1] = col[1]; d[o + 2] = col[2]; d[o + 3] = 255;
  }
  x.putImageData(img, 0, 0);
  return c;
}
// the desert sky: more stars than a city sees, fading into the haze near the plateau
const STARS = [];
{
  const r = rng(263);
  while (STARS.length < 96) {
    const x = Math.floor(r() * W), y = 11 + Math.floor(Math.pow(r(), 1.35) * 100);
    if (Math.hypot(x - MOON.x, y - MOON.y) < 16) continue;
    const tint = r();
    STARS.push({ x, y, k: 11 + Math.floor(r() * 70), ph: r(), b: (0.22 + r() * 0.55) * (1 - y / 150),
      big: r() < 0.07, col: tint < 0.15 ? '#ffe6c0' : tint < 0.3 ? '#c8d8ff' : '#eef0ff' });
  }
}
/** the young Ramadan crescent: low in the west-southwest, lit from below by the sun under the horizon (horns up),
    the rest of the disc faintly lit by earthshine */
function renderMoon() {
  const R = MOON.r, s = 2 * R + 3, c0 = R + 1, p = painter(s, s);
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const d = Math.hypot(i - c0, y - c0);
    if (d > R + 0.3) continue;
    const dark = Math.hypot(i - c0 - 0.6, y - c0 + 3.1) < R - 0.2;              // the shadowed part: a disc nudged upward
    if (dark) { p.set(i, y, d > R - 0.8 ? '#262a48' : '#1c1f3c'); continue; }
    const lit = 1 - Math.hypot(i - c0, y - c0 - R) / (R * 1.2);
    p.set(i, y, dpick(['#d8cfa8', '#efe6c4', '#fff8e0'], 0.3 + lit * 0.8, i, y, 0.6));
  }
  return p.done();
}

/* ================= Thin clouds (tileable streaky bands, moonlit tops, lit bellies) ================= */
const CLOUD_DEFS = [
  { seed: 29, y: 10, h: 34, banks: 2, k: 1, pal: { top: '#2c2c56', body: '#151636', core: '#121332', bot: '#1c1b42' } },
  { seed: 53, y: 58, h: 30, banks: 2, k: 2, pal: { top: '#4a3e6e', body: '#221c46', core: '#1e1942', bot: '#3e2e56' } },
];
function makeCloudLayer(def) {
  const r = rng(def.seed), h = def.h, puffs = [];
  for (let b = 0; b < def.banks; b++) {
    const bx = r() * W, bw = 70 + r() * 110, base = h * (0.4 + r() * 0.4);
    const np = 5 + Math.floor(r() * 5);
    for (let i = 0; i < np; i++) {
      const rx = 10 + r() * 22, ry = 1.6 + r() * 2.4;
      puffs.push({ x: bx + (r() - 0.5) * bw, y: base - r() * ry * 1.6, rx, ry, base: base + ry * 0.2 });
    }
  }
  const D = new Float32Array(W * h);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let best = -9;
    for (const p of puffs) {
      let dx = x - p.x; dx -= Math.round(dx / W) * W;
      const dy = y - p.y;
      let v = 1 - (dx * dx) / (p.rx * p.rx) - (dy * dy) / (p.ry * p.ry);
      if (y > p.base) v -= (y - p.base) * 0.6;
      if (v > best) best = v;
    }
    best += 0.22 * Math.sin(TAU * 7 * x / W + y * 0.9 + def.seed) * Math.sin(TAU * 3 * x / W - y * 0.3);
    D[y * W + x] = best;
  }
  const get = (x, y) => (y < 0 || y >= h) ? -9 : D[y * W + ((x % W) + W) % W];
  const [c, cx] = makeCanvas(W, h);
  const img = cx.createImageData(W, h), d = img.data, P = {};
  for (const k in def.pal) P[k] = hex(def.pal[k]);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    const th = (bayer(x, y) - 0.5) * 0.3, v = D[y * W + x];
    if (v + th <= 0.12) continue;
    let col;
    if (get(x, y - 1) + th <= 0.12) col = P.top;
    else if (get(x, y + 1) + th <= 0.12) col = P.bot;
    else if (v > 0.5 + th) col = P.core;
    else col = P.body;
    const o = (y * W + x) * 4;
    d[o] = col[0]; d[o + 1] = col[1]; d[o + 2] = col[2]; d[o + 3] = 255;
  }
  cx.putImageData(img, 0, 0);
  return { c, y: def.y, k: def.k };
}

/* ================= The monuments as light maps, recoloured by the show =================
   Each group (Menkaure + its queens, Khafre, Khufu + its queens, the Sphinx + the valley temple) is a light map: per
   pixel the stone's brightness L (0..1: its texture times the floodlights' fall-off) and the face it is on (0: east,
   toward us and the show's lamps; 1: south, toward the moon; 2: north, in shade). A palette turns a map into a canvas:
   the show's colours light the east faces, the moon (show off) the south ones. Edges step with the 2-row courses. */
function lmap(x0, y0, w, h) {
  const L = new Float32Array(w * h).fill(-1), F = new Uint8Array(w * h);
  return {
    x0, y0, w, h, L, F,
    set(x, y, v, f = 0) { x = Math.round(x) - x0; y = Math.round(y) - y0; if (x < 0 || y < 0 || x >= w || y >= h) return; L[y * w + x] = v; F[y * w + x] = f; },
  };
}
/** a pyramid seen from the east: rows top..BASE; edges(dd) = [xl, xr] at dd rows below the top, arris(dd) the corner
    between the south face (left of it) and the east face; nArris(dd): the corner to a north face on the right */
function pyramid(m, seed, { top, edges, arris, nArris, cap, block = 6, rough = 0.05, bites = 0, base = BASE, rim = false, dim = 1 }) {
  const Hh = base - top;
  for (let y = top; y <= base; y++) {
    const d = y - top, course = d >> 1, inCap = cap ? d < cap(Math.round(edges(d)[0])) : false;   // the casing's edges don't step
    const dd = inCap ? d : Math.min(Hh, course * 2 + 1);
    let [xl, xr] = edges(dd);
    if (bites) { xl += Math.floor(hash(course, seed, 1) * bites); xr -= Math.floor(hash(course, seed, 2) * bites); }
    xl = Math.round(xl); xr = Math.round(xr);
    const xa = arris ? arris(dd) : -1e9, xn = nArris ? nArris(dd) : 1e9, cx = (xl + xr) / 2, half = Math.max(1, (xr - xl) / 2);
    if (rim) m.set(xr + 1, y, 0.3, 0);                                        // a queen's shadow on the pyramid behind it
    for (let x = xl; x <= xr; x++) {
      if (x >= W) break;
      const f = x < xa ? 1 : x > xn ? 2 : 0;
      let v;
      const capRow = cap ? cap(x) : -1;
      if (d < capRow) {                                                     // the smooth limestone casing
        v = 0.9 + (hash(x, y, seed) - 0.5) * 0.06 - (hash(x >> 1, y >> 2, seed + 5) < 0.12 ? 0.08 : 0);
      } else {
        v = (d & 1) ? 0.68 : 0.84;                                          // each course: its lit face, the shadow under it
        if (!(d & 1) && ((x + (course & 1) * 3 + (course % 3)) % block) === 0) v -= 0.12;   // block joints
        if (hash(Math.floor(x / 3), course, seed + 9) < 0.06) v -= 0.1;     // weathered, missing blocks
        v += (hash(x, y, seed + 3) - 0.5) * rough * 2;
        if (cap && d === capRow) v = 0.5;                                   // the casing's broken lower lip casts a shadow
      }
      if (x === xl || x === xr) v += rim ? (x === xl ? 0.9 : -0.16) : 0.06;    // the edges catch the light (a queen: lit on its left)
      if (rim && (d === 0 || x === xl + 1)) v += 0.5;
      if (Math.abs(x - xa) < 0.5 || Math.abs(x - xn) < 0.5) v += 0.07;      // and so do the arrises
      // floodlit from lamps round the foot: brightest part-way up, the lowest courses in the shadow of the plateau's rim
      const low = rim ? 0.4 : Math.max(0, d - (Hh - 16)) / 16;              // (the queens have lamps of their own)
      const lf = (0.8 + 0.2 * Math.sin(Math.PI * Math.min(1, d / Hh * 1.3))) * (1 - 0.38 * low * low) * (1 - 0.16 * Math.abs(x - cx) / half);
      m.set(x, y, clamp(v * lf * dim, 0, 1), f);
    }
  }
}
/** a step pyramid (Menkaure's two smaller queens): steps [halfWidth, rows] from the top down */
function stepPyramid(m, cx, top, steps, seed) {
  let y = top;
  steps.forEach(([hw, rows], s) => {
    for (let j = 0; j < rows; j++, y++) for (let x = cx - hw; x <= cx + hw; x++) {
      let v = j === 0 ? 0.82 : 0.66 + (hash(x, y, seed) - 0.5) * 0.14;
      if (j === rows - 1) v = 0.52;
      if (hash(x >> 1, s, seed) < 0.15) v -= 0.1;
      m.set(x, y, clamp(v * (0.78 + 0.22 * s / steps.length), 0, 1), x < cx - hw + 2 ? 1 : 0);
    }
  });
}
function buildMenkaure() {
  const m = lmap(0, 100, 104, 40);
  const Hh = BASE - MK.tip;
  pyramid(m, 31, { top: MK.tip, edges: dd => [MK.x - Math.max(1, MK.hw * dd / Hh), MK.x + Math.max(1, MK.hw * dd / Hh)],
    arris: dd => MK.x - MK.hw * dd / Hh + 1.6 * dd / Hh, nArris: dd => MK.x + MK.hw * dd / Hh - 3 * dd / Hh, block: 5 });
  for (let y = BASE - 3; y <= BASE; y++) for (let x = MK.x - MK.hw; x <= MK.x + MK.hw; x++) {   // granite casing left at the foot
    const k = (y - 100) * m.w + x;
    if (m.L[k] >= 0) m.L[k] *= (x + y) & 1 ? 0.78 : 0.86;
  }
  // its three queens beyond it (to the south): G3-a the largest and nearest, G3-b and G3-c built as step pyramids
  stepPyramid(m, 44, 129, [[2, 2], [3, 2], [5, 2], [6, 2]], 7);
  stepPyramid(m, 31, 127, [[2, 2], [4, 2], [5, 2], [7, 2], [8, 2]], 8);
  pyramid(m, 33, { top: 124, edges: dd => [17 - Math.max(1, 10 * dd / 12), 17 + Math.max(1, 10 * dd / 12)], arris: dd => 17 - 10 * dd / 12 + 1.5 * dd / 12, block: 4, rim: true, dim: 0.9 });
  return m;
}
function buildKhafre() {
  const Hh = BASE - KF.tip, m = lmap(KF.x - KF.hw - 1, KF.tip, 2 * KF.hw + 3, Hh + 1);
  const cap = x => 17 + Math.round(2.4 * Math.sin(x * 0.43) + 2.2 * hash(x, 3, 71) + (Math.abs(x - KF.x) > 12 ? 2 : 0));
  pyramid(m, 41, { top: KF.tip, edges: dd => [KF.x - Math.max(1, KF.hw * dd / Hh), KF.x + Math.max(1, KF.hw * dd / Hh)],
    arris: dd => KF.x - KF.hw * dd / Hh + 5 * dd / Hh, cap, block: 6 });
  return m;
}
function buildKhufu() {
  const Hh = BASE - KU.top, m = lmap(KU.l - 1, KU.top - 1, W - KU.l + 1, Hh + 2);
  pyramid(m, 51, { top: KU.top, edges: dd => [KU.x0 - dd * (KU.x0 - KU.l) / Hh, KU.x1 + dd * (KU.r - KU.x1) / Hh],
    arris: dd => 377 - dd * (377 - KU.ar) / Hh, block: 7, rough: 0.09 });
  // its three queens in front of the east face, weathered to rough stepped mounds: G1-a, G1-b, then G1-c (nearest)
  pyramid(m, 52, { top: 122, edges: dd => [449 - Math.max(2, 14 * dd / 14), 449 + Math.max(2, 14 * dd / 14)], arris: dd => 449 - 14 * dd / 14 + 2 * dd / 14, block: 4, bites: 2.2, rough: 0.1, dim: 0.5, rim: true });
  pyramid(m, 53, { top: 125, edges: dd => [425 - Math.max(2, 12 * dd / 11), 425 + Math.max(2, 12 * dd / 11)], arris: dd => 425 - 12 * dd / 11 + 2 * dd / 11, block: 4, bites: 2.6, rough: 0.12, dim: 0.46, rim: true });
  pyramid(m, 54, { top: 121, edges: dd => [402 - Math.max(1, 15 * dd / 15), 402 + Math.max(1, 15 * dd / 15)], arris: dd => 402 - 15 * dd / 15 + 2 * dd / 15, block: 4, bites: 1.4, dim: 0.54, rim: true });
  return m;
}
/** the Sphinx, face on: the nemes headdress (a broad crown, the headband, wings flaring beside the face and cut off
    square at the jaw, striped lappets falling onto the chest either side of the neck), the damaged face between its
    ears, the chest and forelegs with the Dream Stele between the paws; its south flank and back reaching away on the
    left (we stand a little south of its axis); Khafre's valley temple in front of the flank. It has no contour: the
    show's lamps at its feet and the lighting tower on the right catch its top and right edges, its left (south) side
    turns into shadow, the recesses (the neck, round the ears) are dark, and the rock's beds band the body: a hard bed
    juts and catches the light, a soft one is undercut. */
function buildSphinx() {
  const m = lmap(SX - 50, STOP - 1, 72, 42), S = (x, y, v, f = 0) => m.set(SX + x, STOP + y, clamp(v, 0, 1), f);
  const bed = y => [0.1, 0.03, 0, -0.05, -0.14][(y + 40) % 5], chestBed = y => [0.13, 0.02, 0, 0, -0.12][(y + 40) % 5];
  // the flank and back reaching away to the rump (its south side, which the moon lights when the show dims): the
  // haunch swells low at the rear, the belly's shadow lies along the ground
  const backAt = x => x > -30 ? 13 + Math.round((-10 - x) * 0.18) : [17, 17, 18, 20, 23][-30 - x];
  for (let x = -34; x <= -10; x++) for (let y = backAt(x); y <= 30; y++) {
    const haunch = Math.hypot((x + 25) / 6.5, (y - 24) / 3.4);
    let v = 0.78 + bed(y) + (hash(x, y, 17) - 0.5) * 0.06;
    if (y === backAt(x) + 1) v = 0.66;                                      // the back rounding over into the flank
    if (haunch < 1) v += haunch > 0.7 && y < 24 ? 0.14 : 0.06;              // the haunch's curve catches the light
    else if (haunch < 1.3 && y < 24) v -= 0.08;                            // the crease above it
    if (y === 30) v -= 0.16;
    S(x, y, v * (0.84 + 0.16 * (x + 34) / 24), 1);
  }
  // shoulders, chest and forelegs, the paws reaching toward us; the chest's left side turns into the flank's shade
  const chest = y => y < 12 ? -1 : y < 13 ? 7 : y < 14 ? 9 : y < 15 ? 10 : y < 16 ? 11 : y < 21 ? 12 : 13;
  for (let y = 12; y <= 36; y++) {
    const hw = chest(y);
    for (let x = -hw; x <= hw; x++) {
      const a = Math.abs(x), side = x + hw;
      let v = 0.82 + chestBed(y) - 0.14 * Math.max(0, -x / hw) ** 1.5 + (hash(x, y, 19) - 0.5) * 0.03;   // rounded, lit from the right
      if (y >= 31 && a <= 3) v = 0.34;                                     // between the paws
      if (y >= 31 && a >= 4) v = (y === 31 ? 0.94 : 0.8) - (a === 8 ? 0.2 : 0);   // the paws
      if (y === 36 && a >= 4 && (a & 1)) v -= 0.2;                         // toes
      S(x, y, side === 0 ? v - 0.12 : v, side <= 1 ? 1 : 0);
    }
  }
  for (let y = 32; y <= 36; y++) for (let x = -1; x <= 1; x++) if (y > 32 || x === 0) S(x, y, x === 1 ? 0.56 : 0.7);   // the Dream Stele
  // the nemes: crown and wings (half-width per row, the top of the head to the jaw), faintly striped, the headband
  const NEMES = [3, 4, 5, 5, 5, 6, 7, 7, 8, 8, 9];
  NEMES.forEach((hw, y) => { for (let x = -hw; x <= hw; x++) S(x, y, y >= 5 && Math.abs(x) >= 5 && (y & 1) ? 0.76 : 0.87); });
  for (let x = -4; x <= 4; x++) S(x, 3, 0.97);
  // the lappets either side of the neck, striped, their inner edges in the neck's shadow
  for (let y = 11; y <= 16; y++) for (const s of [-1, 1]) for (let a = 4; a <= 6; a++)
    if (y < 16 || a < 6) S(s * a, y, ((y & 1) ? 0.76 : 0.88) - (a === 4 ? 0.16 : 0));
  // the face: brow, eyes under heavy lids, the flat scar where the nose was, full lips, chin; the ears in the shadow of
  // the wings; the neck recessed under the jaw
  const FACE = [                                                            // rows 4..10, columns -4..4
    [0.44, 0.8, 0.72, 0.72, 0.86, 0.72, 0.72, 0.84, 0.46],
    [0.6, 0.82, 0.38, 0.72, 0.9, 0.72, 0.4, 0.86, 0.64],
    [0.72, 0.8, 0.84, 0.82, 0.74, 0.86, 0.88, 0.88, 0.76],
    [0.66, 0.78, 0.82, 0.62, 0.48, 0.66, 0.86, 0.88, 0.7],
    [0.46, 0.76, 0.8, 0.5, 0.44, 0.52, 0.84, 0.86, 0.48],
    [0.4, 0.7, 0.78, 0.84, 0.88, 0.86, 0.82, 0.78, 0.42],
    [0.38, 0.52, 0.6, 0.7, 0.72, 0.72, 0.64, 0.56, 0.4],
  ];
  FACE.forEach((row, j) => row.forEach((v, i) => S(i - 4, 4 + j, v)));
  for (let y = 11; y <= 13; y++) for (let x = -3; x <= 3; x++) S(x, y, 0.36 + (y - 11) * 0.06 + (x > 0 ? 0.04 : 0));
  // the light at its silhouette: the top (the crown and the back) and the right edges catch the lamps; the left sides
  // of the head and chest and the rump's fall turn into the south side's shade
  const inside = (x, y) => x >= 0 && y >= 0 && x < m.w && y < m.h && m.L[y * m.w + x] >= 0;
  const leftEdge = (x, y) => inside(x, y) && !inside(x - 1, y) && y + m.y0 > STOP + 1 && (x + m.x0 - SX > -10 || x + m.x0 - SX < -31);
  const edge = [];
  for (let y = 0; y < m.h; y++) for (let x = 0; x < m.w; x++) {
    const k = y * m.w + x;
    if (leftEdge(x, y)) edge.push([k, Math.min(m.L[k], 0.56), 1]);
    else if (leftEdge(x - 1, y)) edge.push([k, Math.min(m.L[k], 0.64), 1]);
    else if (inside(x, y) && (!inside(x, y - 1) || !inside(x + 1, y))) edge.push([k, Math.max(m.L[k], 0.95), 0]);
  }
  for (const [k, v, f] of edge) { m.L[k] = v; m.F[k] = f; }
  // Khafre's valley temple beside it: a massive flat-topped block, granite-faced, two dark entrances
  for (let y = 26; y <= 36; y++) for (let x = -46; x <= -18; x++) {
    let v = y === 26 ? 0.72 : y === 27 ? 0.42 : 0.56 + (hash(x >> 2, (y - 28) / 3 | 0, 23) - 0.5) * 0.12;
    if (y > 27 && (x + 46) % 5 === 0) v -= 0.1;
    if (y > 27 && (y - 28) % 3 === 2) v -= 0.07;
    if (y >= 31 && (x === -40 || x === -39 || x === -26 || x === -25)) v = 0.2;
    S(x, y, v * (0.58 + 0.24 * (x + 46) / 28), x < -44 ? 1 : 0);
  }
  return m;
}

/* ================= The show's lights ================= */
// a ramp per colour (shadow to highlight) and how each face takes it: [east, south, north]
const SHOW = {
  dim: { ramp: ['#0b0b1a', '#121226', '#1a1a32', '#23223e', '#2d2b4a', '#393556', '#474262'], face: [0.62, 0.98, 0.4] },
  gold: { ramp: ['#24140e', '#3e2414', '#64381a', '#925424', '#c07a34', '#e2a44e', '#f8d282', '#fff0c0'], face: [1, 0.58, 0.34] },
  amber: { ramp: ['#2a160c', '#4a2612', '#743e18', '#a45c22', '#cc8232', '#eca84c', '#ffd27e', '#fff0c4'], face: [1, 0.66, 0.4] },
  blue: { ramp: ['#080c2a', '#0e1a48', '#162c6c', '#204494', '#2e60ba', '#4c86da', '#7eb0f0', '#c0dcff'], face: [1, 0.6, 0.34] },
  magenta: { ramp: ['#1c0822', '#34103c', '#54185c', '#7c2682', '#a23ca2', '#c65cbc', '#e690d8', '#ffc8f0'], face: [1, 0.6, 0.34] },
  green: { ramp: ['#061a16', '#0a2c22', '#104230', '#185e40', '#267e52', '#40a068', '#72c890', '#b4ecc4'], face: [1, 0.6, 0.34] },
  red: { ramp: ['#220806', '#3e100a', '#621a0e', '#8c2814', '#b83e1c', '#dc6232', '#f4945c', '#ffc898'], face: [1, 0.6, 0.34] },
  violet: { ramp: ['#120a2a', '#1e1244', '#2c1c62', '#402a82', '#563ea2', '#7258c0', '#9a84dc', '#cab8f4'], face: [1, 0.6, 0.34] },
  white: { ramp: ['#2a2420', '#4a4036', '#6e604e', '#968468', '#bca888', '#dccaa8', '#f4e6c8', '#fffaec'], face: [1, 0.7, 0.45] },
};
/** a light map in a show colour: the ramp dithered by L times its face's share of the light */
function paintMap(m, name) {
  const { ramp, face } = SHOW[name], p = painter(m.w, m.h);
  for (let y = 0; y < m.h; y++) for (let x = 0; x < m.w; x++) {
    const k = y * m.w + x, v = m.L[k];
    if (v < 0) continue;
    p.set(x, y, dpick(ramp, v * face[m.F[k]] + 0.04, x + m.x0, y + m.y0, 0.8));
  }
  return p.done();
}
// Eight half-minute scenes of the show; the eighth is the first again, so the loop joins without a change. Each new
// colour sweeps across the plateau (left to right, then back), a bright seam at its edge. Scene 3: the pyramids go
// dark and a spotlight finds the Sphinx, which "speaks".
const SCENE_LEN = 30, SWEEP = 6;
const PHASES = [
  { M: 'gold', A: 'gold', U: 'gold', S: 'amber' },
  { M: 'gold', A: 'gold', U: 'blue', S: 'amber' },
  { M: 'blue', A: 'magenta', U: 'blue', S: 'amber' },
  { M: 'dim', A: 'dim', U: 'dim', S: 'white' },
  { M: 'green', A: 'green', U: 'gold', S: 'amber' },
  { M: 'red', A: 'red', U: 'gold', S: 'amber' },
  { M: 'violet', A: 'blue', U: 'violet', S: 'blue' },
  { M: 'gold', A: 'gold', U: 'gold', S: 'amber' },
];
const SPEAKS = [3 * SCENE_LEN, 4 * SCENE_LEN];       // the Sphinx's spotlit monologue
/** the sweep's edge (scene x) at loop time t, its direction, and the scene it is bringing in (null: none running) */
function sweepAt(t) {
  const k = Math.floor(t / SCENE_LEN), u = t - k * SCENE_LEN;
  if (u >= SWEEP || k === 0) return { k, from: k, x: null, dir: 1 };
  const dir = k & 1 ? -1 : 1, e = smooth(0, SWEEP, u), x = dir > 0 ? lerp(-12, W + 12, e) : lerp(W + 12, -12, e);
  return { k, from: k - 1, x, dir };
}

/* ================= The desert beyond the pyramids, and the plateau in front of them ================= */
function renderDesert() {
  const p = painter(W, 12), top = x => 3 + Math.round(1.6 * Math.sin(x * 0.021 + 1) + 1.2 * Math.sin(x * 0.067) + 0.8 * Math.sin(x * 0.19));
  for (let x = 0; x < W; x++) for (let y = top(x); y < 12; y++)
    p.set(x, y, y === top(x) ? '#3a2e44' : dpick(['#2a2238', '#231d32', '#1d182c'], (y - top(x)) / 8, x, y));
  return p.done();                                                        // drawn at y 126: the ridge line in the gaps
}
const PLAT_Y = 136;
function renderPlateau() {
  const p = painter(W, 36), r = rng(97), S = (x, y, col) => p.set(x, y - PLAT_Y, col);
  // the plateau's surface, lighter where the show's lamps spill onto it, rubble and sand ripples
  for (let y = PLAT_Y; y < PLAT_Y + 36; y++) for (let x = 0; x < W; x++) {
    const spill = Math.max(0, 1 - Math.hypot((x - SX) / 70, (y - 140) / 14)) * 0.35 + Math.max(0, 1 - Math.abs(x - 380) / 120) * 0.12;
    const v = 0.72 - (y - PLAT_Y) / 30 + spill + (hash(x, y, 5) - 0.5) * 0.12;
    S(x, y, dpick(['#1e1824', '#28202c', '#342932', '#44343a', '#56443e'], v, x, y, 0.7));
  }
  for (let k = 0; k < 90; k++) { const x = Math.floor(r() * W), y = PLAT_Y + 1 + Math.floor(r() * 6); S(x, y, r() < 0.5 ? '#6a5448' : '#1a1420'); }   // rubble at the pyramids' feet
  // the mastabas of the eastern cemetery in front of Khufu's queens: low flat-topped tombs in rows
  for (let bx = 368; bx < W - 6;) {
    const w = 7 + Math.floor(r() * 8), h = 2 + Math.floor(r() * 3), y0 = PLAT_Y + 1 + Math.floor(r() * 3);
    for (let x = bx; x < bx + w; x++) { S(x, y0, '#7a6454'); for (let y = y0 + 1; y <= y0 + h; y++) S(x, y, x === bx ? '#2e2430' : '#4a3a3c'); }
    bx += w + 2 + Math.floor(r() * 5);
  }
  // the Sphinx's enclosure: the quarried scarp behind it, its sloping side walls, the trench floor
  for (let x = 150; x <= 238; x++) {
    const top = 134 + Math.round(Math.abs(x - SX) > 30 ? (Math.abs(x - SX) - 30) * 0.12 : 0) + (hash(x, 1, 7) < 0.3 ? 1 : 0);
    for (let y = top; y < 146; y++) {
      let col = y === top ? '#7a6252' : dpick(['#3a2e36', '#4a3a3c', '#5a463e'], 0.7 - (y - top) / 16 + (hash(x >> 1, y, 8) - 0.5) * 0.3, x, y, 0.6);
      if ((x * 7 + 3) % 11 === 0 && y > top + 1) col = '#2a2230';           // crevices in the soft rock
      S(x, y, col);
    }
  }
  for (let y = 146; y < 154; y++) for (let x = 150; x <= 238; x++) S(x, y, dpick(['#2a2230', '#342a32'], 0.5 + (hash(x, y, 9) - 0.5) * 0.4, x, y));
  // the show's stands in front of the Sphinx: rows of seats seen from behind, aisles, the lighting tower at the end
  for (let row = 0; row < 8; row++) {
    const y = 154 + row * 2, x0 = 160 - row * 2, x1 = 236 + row;
    for (let x = x0; x <= x1; x++) {
      const aisle = Math.abs(x - 198) <= 1 || Math.abs(x - 174) <= 0 || Math.abs(x - 222) <= 0;
      S(x, y, aisle ? '#2e2834' : (x & 1) ? '#3e3444' : '#34303e');
      S(x, y + 1, aisle ? '#28222e' : '#16121c');
    }
  }
  for (let y = 138; y < 170; y++) { S(242, y, '#3a3844'); S(243, y, '#24222c'); }                 // the lighting tower
  for (let x = 239; x <= 246; x++) { S(x, 137, '#4a4854'); S(x, 138, '#2a2830'); }
  for (let y = 139; y < 170; y += 4) for (let x = 240; x <= 245; x++) if ((x + y) % 3 === 0) S(x, y, '#2e2c36');
  return p.done();
}

/* ================= Nazlet el-Samman: brick-and-concrete houses along the lane, unfinished roofs ================= */
const MAT = {
  brick: ['#160b0c', '#23110f', '#331811', '#462114', '#5c2b18', '#74381e', '#8c4626'],
  ochre: ['#16100e', '#221a16', '#30261e', '#403226', '#524230', '#66523a', '#7a6446'],
  blue: ['#10121a', '#181c26', '#222834', '#2e3644', '#3c4654', '#4c5866', '#5e6a78'],
  pink: ['#1a1014', '#261a1e', '#342428', '#443034', '#563c40', '#6a4a4c', '#7e5a5a'],
};
const CONC = ['#121118', '#1c1a22', '#282630', '#36343e', '#46444e', '#58566a', '#6c6a7a'];
const GROUND = 184;                                   // the shops' lintels: ground floors below, flats above
const BLD = [
  { x0: 0, x1: 36, roof: 150, mat: 'ochre', cols: [0, 17, 34], shop: 'cafe' },
  { x0: 36, x1: 90, roof: 144, mat: 'brick', cols: [36, 54, 72, 88], shop: 'lanterns', top: true, crescent: [63, 158] },
  { x0: 90, x1: 154, roof: BB_ROOF, mat: 'brick', cols: [90, 106, 122, 138, 152], shop: 'sweets', top: true },
  { x0: 248, x1: 292, roof: 156, mat: 'pink', cols: [248, 262, 276, 290], shop: 'stable' },
  { x0: 292, x1: 350, roof: 146, mat: 'brick', cols: [292, 310, 330, 348], shop: 'bazaar', top: true },
  { x0: 350, x1: 414, roof: 152, mat: 'blue', cols: [350, 366, 382, 398, 412], shop: 'juice' },
  { x0: 414, x1: 480, roof: 144, mat: 'brick', cols: [414, 430, 446, 462, 478], shop: 'stable2', top: true },
];
// where the lane's light falls on the facades: a warm wash rising from the shops, pools under the lanterns
const facadeLight = (x, y) => clamp(0.16 + 0.5 * smooth(150, GROUND + 6, y) + 0.25 * Math.max(0, 1 - Math.hypot(x - 63, (y - 186) * 1.4) / 40)
  + 0.15 * Math.max(0, 1 - Math.hypot(x - 200, (y - 180) * 1.6) / 60), 0, 1);
function renderVillage() {
  const p = painter(W, H, true), r = rng(404), wins = [], bulbs = [], tvs = [], shops = [];
  BLD.forEach((b, bi) => {
    p.id = bi;
    const pal = MAT[b.mat], isCol = x => b.cols.some(c => x >= c && x < c + 2);
    for (let y = b.roof; y < LANE - 2; y++) for (let x = b.x0; x < b.x1; x++) {
      const L = facadeLight(x, y), slab = y < GROUND && (GROUND - y) % 9 === 0;
      let col;
      if (isCol(x) || slab || y === b.roof) col = dpick(CONC, 0.28 + L * 0.7 + (y === b.roof ? 0.12 : 0), x, y, 0.7);
      else if (b.mat === 'brick') {
        const mortar = (y & 1) || ((x + ((y >> 1) & 1) * 2) & 3) === 0;
        col = dpick(pal, 0.2 + L * 0.72 - (mortar ? 0.1 : 0) + (hash(x >> 1, y, bi) - 0.5) * 0.1, x, y, 0.8);
      } else col = dpick(pal, 0.22 + L * 0.7 + (hash(x >> 2, y >> 1, bi + 20) < 0.12 ? -0.12 : 0), x, y, 0.8);
      p.set(x, y, col);
    }
    // windows (lit per frame), a few with wooden shutters, balconies with a railing and laundry on some
    for (let slab = GROUND; slab - 9 >= b.roof; slab -= 9) {
      const wy = slab - 6;
      for (let k = 0; k + 1 < b.cols.length; k++) {
        const bx0 = b.cols[k] + 2, bx1 = b.cols[k + 1], bw = bx1 - bx0;
        const nWin = bw >= 14 ? 2 : 1;
        for (let j = 0; j < nWin; j++) {
          const wx = Math.round(bx0 + (bw * (j + 1)) / (nWin + 1)) - 1, kind = r();
          for (let y = wy; y < wy + 4; y++) for (let x = wx; x < wx + 3; x++) p.set(x, y, '#0e0a12');
          for (let x = wx - 1; x <= wx + 3; x++) p.set(x, wy - 1, dpick(CONC, 0.4 + facadeLight(x, wy) * 0.5, x, wy));   // lintel
          if (kind < 0.18) for (let y = wy; y < wy + 4; y++) for (let x = wx; x < wx + 3; x++) p.set(x, y, (y & 1) ? '#3a2618' : '#4e3420');   // shutters
          else if (kind < 0.36) tvs.push({ id: tvs.length, x: wx, y: wy, ph: r() });                 // a TV in the room: Ramadan's series
          else if (kind < 0.9) wins.push({ id: wins.length, x: wx, y: wy, b: bi, n: 1 + Math.floor(r() * 5), p: 0.35 + r() * 0.45, off: r(), style: Math.floor(r() * 5),
            col: r() < 0.3 ? '#d6f0e2' : r() < 0.5 ? '#ffd890' : '#ffc070' });
        }
        if (slab < GROUND && r() < 0.35) {                                   // a balcony
          for (let x = bx0; x < bx1; x++) { p.set(x, slab, '#58566a'); if ((x - bx0) % 2 === 0) p.set(x, slab - 1, '#3a3842'); p.set(x, slab - 3, '#46444e'); }
          if (r() < 0.6) for (let x = bx0 + 1; x < bx1 - 1; x++) if (hash(x, slab, 3) < 0.55) p.set(x, slab - 2 + (hash(x, slab, 4) < 0.5 ? 0 : 1), ['#c8c0b8', '#8a4a4a', '#4a6a9a', '#d8b870', '#6a8a5a'][Math.floor(hash(x >> 1, slab, 5) * 5)]);   // laundry
          if (r() < 0.55) for (let x = bx0; x < bx1; x += 2) bulbs.push({ x, y: slab - 4, k: bulbs.length });   // Ramadan string lights
        }
      }
    }
    // the ground floor: a lit shopfront under a lintel (drawn per frame by kind), metal shutters rolled up
    for (let x = b.x0; x < b.x1; x++) { p.set(x, GROUND, '#46444e'); p.set(x, GROUND + 1, '#24222a'); }
    shops.push({ kind: b.shop, x0: b.x0 + 2, x1: b.x1 - 2, y0: GROUND + 2, y1: LANE - 4 });
    // the roof: unfinished columns bristling with rebar, water tanks, dishes all aimed the same way, a rooftop room
    if (b.top) for (const c of b.cols) {
      for (let y = b.roof - 5; y < b.roof; y++) { p.set(c, y, CONC[3]); p.set(c + 1, y, CONC[2]); }
      for (const [dx, h] of [[0, 4], [1, 3], [-1, 3], [2, 5]]) for (let y = b.roof - 5 - h; y < b.roof - 5; y++) p.set(c + dx + (y < b.roof - 7 ? Math.sign(dx) : 0), y, (y & 1) ? '#6a3e2a' : '#8a5236');
    }
    for (let x = b.x0; x < b.x1; x++) p.set(x, b.roof - 1, (x & 1) ? CONC[2] : CONC[1]);            // the parapet
  });
  p.id = -2;
  const tank = (x, y, white) => {                                           // a water tank on its stand
    for (let j = 0; j < 6; j++) for (let i = 0; i < 6; i++) p.set(x + i, y + j, white ? (i === 0 ? '#e8ecf4' : i < 4 ? '#b8c0d0' : '#7a8498') : (i === 1 ? '#4a4a58' : '#1a1a24'));
    for (let i = 0; i < 6; i++) p.set(x + i, y - 1, white ? '#8a94a8' : '#2a2a36');
    p.set(x, y + 6, '#2a2830'); p.set(x + 5, y + 6, '#2a2830'); p.set(x, y + 7, '#2a2830'); p.set(x + 5, y + 7, '#2a2830');
  };
  const dish = (x, y) => {                                                  // a satellite dish aimed up-left (south, at the satellite)
    paintRows(p, ['.aab.', 'aaab.', 'aabb.', '.bb.c', '...c.', '..cc.'], { a: '#a8a6b8', b: '#6a687a', c: '#3a3844' }, x, y);
  };
  for (const [x, y, w] of [[18, 144, 0], [48, 136, 1], [100, 132, 0], [300, 140, 1], [356, 146, 0], [428, 138, 1], [456, 138, 0]]) tank(x, y - 1, w);
  for (const [x, y] of [[6, 144], [28, 144], [60, 138], [80, 138], [112, 132], [128, 132], [316, 140], [334, 140], [372, 146], [392, 146], [404, 146], [440, 138], [468, 138]]) dish(x, y - 6);
  for (let y = 148; y < 156; y++) for (let x = 292 - 12; x < 290; x++) if (x >= 280) p.set(x, y, dpick(MAT.pink, 0.3, x, y));   // stair room on the low roof
  // a laundry line across the roof of the tallest house on the left
  for (let x = 56; x < 84; x++) { const y = 138 + Math.round(1.5 * Math.sin(Math.PI * (x - 56) / 28)); p.set(x, y, '#1a1620'); if (hash(x, 9) < 0.5 && x % 3) p.set(x, y + 1, ['#d8d0c8', '#9a4a4a', '#4a6aa0'][x % 3]); }
  const vis = wins.filter(w => p.own[w.y * W + w.x] === w.b);
  vis.forEach((w, i) => { w.id = i; });
  return { c: p.done(), wins: vis, bulbs, tvs, shops };
}

/* ================= The lane's shopfronts (static art; their lights move per frame) ================= */
const FANOUS_COLS = ['#ff5a4a', '#ffd04a', '#5ae07a', '#5aa8ff', '#ff7ad0', '#ffa040'];
function renderShops(p, shops) {
  const lanterns = [], r = rng(515);
  for (const s of shops) {
    const { x0, x1, y0, y1 } = s, S = (x, y, col) => p.set(x, y, col);
    const fill = (pal, v = () => 0.5) => { for (let y = y0; y <= y1; y++) for (let x = x0; x <= x1; x++) S(x, y, dpick(pal, v(x, y), x, y)); };
    if (s.kind === 'cafe') {                                               // an ahwa under a fluorescent tube, men at its tables
      fill(['#5a7a6e', '#7a9a8a', '#a8c8b8'], (x, y) => 0.9 - (y - y0) / 14);
      for (let x = x0; x <= x1; x++) S(x, y0, '#e8fff4');
      for (const [x, shirt] of [[6, '#e8e4dc'], [13, '#6a5a4a'], [24, '#3a4a6a']]) {
        S(x, y0 + 4, '#1a1418'); S(x, y0 + 5, '#8a6048'); for (let y = y0 + 6; y <= y1 - 1; y++) { S(x, y, shirt); S(x + 1, y, shirt); }
      }
      for (const x of [9, 20, 29]) { S(x, y1 - 3, '#d8d0c0'); S(x + 1, y1 - 3, '#d8d0c0'); S(x, y1 - 2, '#6a6060'); S(x, y1 - 1, '#6a6060'); }   // tables
      for (let y = y0 + 2; y <= y0 + 4; y++) for (let x = 26; x <= 31; x++) S(x, y, '#141820');   // the TV (lit per frame)
    } else if (s.kind === 'lanterns') {                                    // a fanous stall: rows of lanterns for sale
      fill(['#2a140e', '#4a2416', '#6a3a1e'], (x, y) => 0.3 + (y - y0) / 20);
      for (let row = 0; row < 3; row++) for (let x = x0 + 2 + (row & 1) * 2; x < x1 - 1; x += 5)
        lanterns.push({ x, y: y0 + 1 + row * 4, col: FANOUS_COLS[Math.floor(r() * FANOUS_COLS.length)], id: lanterns.length, big: false });
      for (let x = x0 + 1; x < x1; x += 7) lanterns.push({ x, y: LANE - 8, col: FANOUS_COLS[Math.floor(r() * FANOUS_COLS.length)], id: lanterns.length, big: true });   // big ones on the pavement
    } else if (s.kind === 'sweets') {                                      // trays of konafa, basbousa and qatayef under a bright lamp
      fill(['#6a4020', '#a86a30', '#e8b060', '#ffe0a0'], (x, y) => 0.95 - (y - y0) / 13);
      for (let x = x0 + 2; x < x1 - 2; x++) { S(x, y0 + 6, '#b8b0a8'); S(x, y0 + 7, '#5a4a40'); }   // the counter's glass
      for (let k = 0; k < 7; k++) {
        const cx = x0 + 5 + k * 8, col = ['#e8a030', '#c87a28', '#f0c060'][k % 3];
        for (let x = cx - 3; x <= cx + 3; x++) { S(x, y0 + 5, col); S(x, y0 + 4, Math.abs(x - cx) < 3 ? mixHex(col, '#fff0c0', 0.3) : col); }
        if (k % 3 === 0) { S(cx - 1, y0 + 4, '#6ab04a'); S(cx + 1, y0 + 4, '#6ab04a'); }        // pistachio on the konafa
      }
      for (let x = x0; x <= x1; x++) S(x, y0, '#fff4d0');
    } else if (s.kind === 'stable' || s.kind === 'stable2') {              // a stable gate, a horse looking out
      fill(['#0e0a0c', '#1a1216', '#2a1c18'], (x, y) => 0.2 + Math.max(0, 1 - Math.abs(x - (x0 + x1) / 2) / 16) * 0.5);
      const cx = Math.round((x0 + x1) / 2) + (s.kind === 'stable' ? -6 : 8);
      for (let y = y0; y < y0 + 3; y++) for (let x = x0; x <= x1; x++) if (Math.abs(x - cx) > 8 - (y - y0) * 2) S(x, y, dpick(MAT.pink, 0.4, x, y));
      paintRows(p, ['..ee...', '.hhhh..', 'hhhhhhh', '.hhhhhh', '..hhh.h', '..hh...', '..hh...'], { e: '#3a241a', h: '#5a3a26' }, cx - 3, y0 + 3);   // a horse's head
      for (let x = x0 + 2; x < x0 + 9; x++) for (let y = y1 - 3; y <= y1; y++) S(x, y, ((x + y) & 1) ? '#8a7040' : '#6a5230');   // hay
    } else if (s.kind === 'bazaar') {                                      // papyrus paintings, little pyramids and cats of stone
      fill(['#4a2a18', '#7a4a24', '#b07838'], (x, y) => 0.7 - (y - y0) / 20);
      for (let k = 0; k < 6; k++) {
        const px0 = x0 + 2 + k * 9;
        for (let y = y0 + 1; y <= y0 + 5; y++) for (let x = px0; x < px0 + 6; x++) S(x, y, y === y0 + 1 || x === px0 ? '#3a2410' : ['#e8d8a8', '#d8c090'][(x + y) & 1]);
        S(px0 + 2, y0 + 3, ['#2a6ab0', '#c83a2a', '#2a8a4a'][k % 3]); S(px0 + 3, y0 + 3, '#1a1410'); S(px0 + 3, y0 + 4, '#c8a040');
      }
      for (let k = 0; k < 5; k++) { const cx = x0 + 6 + k * 11; for (let j = 0; j < 4; j++) for (let x = cx - j; x <= cx + j; x++) S(x, y1 - 3 + j, j === 0 ? '#ffe0a0' : x < cx ? '#e0b060' : '#a87a40'); }
    } else if (s.kind === 'juice') {                                       // a juice bar: fruit hanging round a bright counter
      fill(['#8ab0a0', '#c8e8d8', '#f0fff8'], (x, y) => 0.85 - (y - y0) / 12);
      for (let x = x0; x <= x1; x++) if (hash(x, 3, 1) < 0.8) S(x, y0 + (hash(x, 4, 1) < 0.5 ? 0 : 1), ['#f8a020', '#f8d030', '#6ac040', '#e84a3a'][Math.floor(hash(x, 5, 1) * 4)]);
      for (let k = 0; k < 4; k++) { const x = x0 + 8 + k * 14; for (let y = y0 + 4; y <= y1 - 2; y++) { S(x, y, '#e8e8f0'); S(x + 1, y, '#9aa8b0'); } S(x, y0 + 4, '#f8a020'); S(x + 1, y0 + 4, '#6ac040'); }   // glasses of cane juice
      for (let x = x0 + 30; x < x0 + 42; x++) for (let y = y0 + 3; y <= y1; y++) S(x, y, (x & 1) ? '#5a6a60' : '#3a4a40');   // the cane press
    }
  }
  return lanterns;
}

/* ================= The minaret: a modern village minaret, green tubes on its shafts and balconies ================= */
const MIN_BOX = { x: MIN.x - 7, y: MIN.tip, w: 15, h: 92 };
function renderMinaret() {
  const st = painter(MIN_BOX.w, MIN_BOX.h), gr = painter(MIN_BOX.w, MIN_BOX.h), c0 = 7;
  const S = (x, y, col) => st.set(x + c0, y - MIN.tip, col), G = (x, y, col = '#5aff8a') => gr.set(x + c0, y - MIN.tip, col);
  const shaft = (y0, y1, hw) => { for (let y = y0; y <= y1; y++) for (let x = -hw; x <= hw; x++) S(x, y, x === -hw ? '#6a6674' : x === hw ? '#24222c' : x < 0 ? '#4a4652' : '#36343e'); };
  const balcony = (y, hw) => {
    for (let x = -hw; x <= hw; x++) { S(x, y, (x & 1) ? '#5a5664' : '#2a2832'); S(x, y + 1, x < 0 ? '#6a6674' : '#46424e'); G(x, y + 1); }
    for (let k = 1; k <= 2; k++) for (let x = -hw + k; x <= hw - k; x++) S(x, y + 1 + k, k === 1 ? '#3a3844' : '#2a2832');   // corbels
    for (let x = -hw; x <= hw; x += 2) G(x, y, '#9affc0');
  };
  shaft(105, 152, 3); shaft(81, 100, 2); shaft(72, 77, 2);
  for (const y of [118, 136]) { S(0, y, '#1a1a22'); S(0, y + 1, '#ffe0a0'); S(0, y + 2, '#ffe0a0'); S(-1, y + 1, '#1a1a22'); S(1, y + 1, '#1a1a22'); }   // little arched windows, lit
  for (let y = 106; y <= 150; y++) { G(-3, y); G(3, y, '#3ad06a'); }       // the green tubes up the shaft
  for (let y = 82; y <= 99; y++) { G(-2, y); G(2, y, '#3ad06a'); }
  balcony(101, 5); balcony(78, 4);
  for (let y = 73; y <= 76; y++) S(0, y, '#12121a');                        // the top pavilion's open arch
  [[67, 0], [68, 1], [69, 1], [70, 2], [71, 2]].forEach(([y, hw]) => { for (let x = -hw; x <= hw; x++) S(x, y, x < 0 ? '#6a6674' : '#46424e'); G(-hw, y); G(hw, y); });   // the bulb
  for (let y = 64; y <= 66; y++) S(0, y, '#8a8898');
  G(-1, 62, '#d8ffe8'); G(1, 62, '#d8ffe8'); G(-1, 63, '#b8ffd0'); G(0, 63, '#b8ffd0'); G(1, 63, '#b8ffd0');   // the crescent, horns up
  return { st: st.done(), gr: gr.done() };
}

/* ================= The pigeon loft on the low roof: a slatted cage on legs, a flag on a pole ================= */
function renderDovecote(p) {
  const { x0, x1, top, roof } = DOVE;
  p.id = -2;
  for (let y = top + 11; y < roof; y++) for (const x of [x0 + 2, x1 - 3]) { p.set(x, y, '#3a2a20'); p.set(x + 1, y, '#1e1612'); }   // legs
  pline(p, x0 + 3, roof - 1, x1 - 3, top + 12, '#2a1e18'); pline(p, x1 - 3, roof - 1, x0 + 3, top + 12, '#2a1e18');
  for (let y = top + 2; y <= top + 11; y++) for (let x = x0; x <= x1; x++) {
    const post = (x - x0) % 5 === 0 || x === x1, rail = y === top + 2 || y === top + 11 || y === top + 6;
    p.set(x, y, post || rail ? (x < (x0 + x1) / 2 ? '#7a5a40' : '#5a4230') : ((x + y) & 1) ? '#2a1e16' : '#120e0e');
  }
  for (let x = x0 - 2; x <= x1 + 2; x++) { p.set(x, top, '#8a6a4a'); p.set(x, top + 1, '#3a2a20'); }   // the flat top the keeper stands on
  for (const [x, d] of [[x0 - 1, -1], [x1 + 1, 1]]) for (let k = 0; k < 4; k++) p.set(x + d * k, top + 7, '#6a4a34');   // perches
  for (let y = top - 18; y < top; y++) p.set(x1 - 1, y, y < top - 16 ? '#c8c0b0' : '#6a5a4a');   // the flagpole
}

/* ================= Zeena: paper pennants on strings across the lane (4 flutter frames each) ================= */
const ZEENA_COLS = ['#e8403a', '#f8d040', '#3ab85a', '#3a7ae0', '#f070b0', '#f89a30', '#f0ece0'];
// [x0, y0, x1, y1, sag, size (0 far, 1 mid, 2 near), light (0..1)]
const STRANDS = [
  [0, 168, 40, 172, 4, 0, 0.7], [38, 166, 90, 160, 5, 0, 0.75], [90, 158, 154, 164, 5, 0, 0.7],
  [152, 166, 250, 170, 12, 1, 0.6], [120, 176, 196, 178, 7, 1, 0.8], [250, 166, 296, 162, 4, 0, 0.6],
  [294, 160, 352, 168, 6, 0, 0.7], [350, 170, 414, 162, 6, 0, 0.7], [414, 164, 480, 170, 5, 0, 0.72],
  [236, 178, 330, 176, 8, 1, 0.8], [330, 180, 480, 174, 9, 1, 0.8], [0, 176, 118, 180, 8, 1, 0.8],
  [-10, 13, 490, 8, 21, 2, 0.55],
];
const strandPt = (s, f) => [lerp(s[0], s[2], f), lerp(s[1], s[3], f) + s[4] * Math.sin(Math.PI * f)];
// fanous lanterns hung from some strands: [strand, position along it]
const STRAND_LANTERNS = [[3, 0.5], [4, 0.3], [4, 0.72], [9, 0.45], [10, 0.3], [10, 0.7], [11, 0.25], [11, 0.65], [1, 0.5], [7, 0.5], [12, 0.24], [12, 0.62]];
function renderStrands() {
  return STRANDS.map((s, si) => {
    const size = s[5], lit = s[6], sp = [3, 4, 6][size], tri = [2, 3, 4][size];
    const xs = [], ys = [], n = Math.ceil(Math.abs(s[2] - s[0]));
    for (let k = 0; k <= n; k++) { const [x, y] = strandPt(s, k / n); xs.push(x); ys.push(y); }
    const bx = Math.floor(Math.min(...xs)) - 3, by = Math.floor(Math.min(...ys)) - 1, bw = Math.ceil(Math.max(...xs)) - bx + 5, bh = Math.ceil(Math.max(...ys)) - by + tri + 3;
    const frames = [0, 1, 2, 3].map(fr => {
      const p = painter(bw, bh);
      for (let k = 0; k <= n; k++) p.set(xs[k] - bx, ys[k] - by, size === 2 ? '#2a2026' : '#1e1820');
      for (let k = 2, j = 0; k <= n - 2; k += sp, j++) {
        const x = Math.round(xs[k]) - bx, y = Math.round(ys[k]) - by + 1, col0 = ZEENA_COLS[Math.floor(hash(si, j, 3) * ZEENA_COLS.length)];
        const st = Math.floor(hash(si * 100 + j, fr, 7) * 5), back = st === 4, lean = st === 1 ? -1 : st === 2 ? 1 : 0;
        const col = mixHex(back ? mixHex(col0, '#1a1020', 0.4) : col0, '#140e18', 1 - lit);
        for (let row = 0; row < tri; row++) {
          const w = tri - row, off = Math.round(lean * row / Math.max(1, tri - 1));
          for (let i = 0; i < w; i++) p.set(x + i - (w >> 1) + off + (tri > 2 && row === 0 ? 0 : 0), y + row, row === 0 && !back ? mixHex(col, '#ffffff', 0.12) : col);
        }
      }
      return p.done();
    });
    return { x: bx, y: by, frames, ph: Math.floor(hash(si, 9) * 4), size };
  });
}

/* ================= Rooftop billboard: a steel sign on the tallest house, lamps on arms above its face ================= */
const BB_FR = { x: BB.x - 6, y: BB.y - 7, w: BB.w + 12, h: BB_ROOF - BB.y + 8 };   // the frame sprite's box: legs down to the roof
const BB_LAMPS = [BB.x + 7, BB.x + (BB.w >> 1), BB.x + BB.w - 8];
const STEEL = ['#6a6a86', '#4a4a66', '#32324a', '#222236', '#16162a'];
function renderBillboardFrame() {
  const p = painter(BB_FR.w, BB_FR.h), S = (x, y, col) => p.set(x - BB_FR.x, y - BB_FR.y, col);
  const x0 = BB.x - 1, x1 = BB.x + BB.w, y0 = BB.y - 1, y1 = BB.y + BB.h;
  for (let i = x0 - 1; i <= x1 + 1; i++) for (let y = y0 - 1; y <= y1 + 1; y++) {
    const ring = i < x0 || i > x1 || y < y0 || y > y1;
    if (ring) S(i, y, STEEL[3]);
    else if (i === x0 || i === x1 || y === y0 || y === y1) S(i, y, i === x0 || y === y0 ? STEEL[1] : STEEL[2]);
  }
  const LEGS = [x0 + 3, BB.x + (BB.w >> 1) - 1, x1 - 4];
  for (const lx of LEGS) for (let y = y1 + 2; y < BB_ROOF; y++) { S(lx, y, STEEL[2]); S(lx + 1, y, STEEL[3]); }
  for (let k = 0; k + 1 < LEGS.length; k++) {                                              // X bracing
    const a = LEGS[k] + 2, b = LEGS[k + 1] - 1;
    for (let i = a; i <= b; i++) { const f = (i - a) / (b - a); S(i, Math.round(lerp(y1 + 4, BB_ROOF - 2, f)), STEEL[3]); S(i, Math.round(lerp(BB_ROOF - 2, y1 + 4, f)), STEEL[3]); }
  }
  for (let i = x0 - 2; i <= x1 + 2; i++) S(i, y1 + 2, (i & 1) ? '#5a5670' : '#46425a');      // catwalk
  for (const lx of BB_LAMPS) {                                                             // lamp arms over the top, heads tipped down
    for (let y = y0 - 4; y < y0 - 1; y++) S(lx, y, STEEL[2]);
    S(lx - 1, y0 - 7, STEEL[1]); S(lx, y0 - 7, STEEL[1]); S(lx + 1, y0 - 7, STEEL[2]); S(lx - 1, y0 - 6, STEEL[2]); S(lx, y0 - 6, STEEL[1]); S(lx + 1, y0 - 6, STEEL[3]);
    S(lx - 1, y0 - 5, '#e8d8b0'); S(lx, y0 - 5, '#fff8e0'); S(lx + 1, y0 - 5, '#d8c8a0');   // the housing, its lit face tipped down
  }
  return p.done();
}
// the city's own ad: a konafa shop (made up): a round tray of konafa, KONAFA, then QATAYEF or the same word in Arabic,
// a border of little zeena pennants, on a deep Ramadan red
const AD_BG = ['#6a1420', '#5a0f1c', '#4a0c18', '#3c0914', '#300710'];
const TRAY_ROWS = [
  '....oooooooo....',
  '..ooOOYOOYOOoo..',
  '.oOYOYgGgYOYOOo.',
  'oOYOYgGGGgYOYOOo',
  'oOOYOYgggYOYOOoo',
  '.ooOOYOOYOOOoo..',
  '..bbooooooooob..',
  '...bbbbbbbbbb...',
];
const TRAY_KEY = { o: '#b86a1c', O: '#e8962c', Y: '#ffc84a', g: '#4a9a3a', G: '#8ad05a', b: '#8a8a98' };
// konafa in Arabic (كنافة) in a small naskh, read right to left: kaf with its slanted top, nun (a tooth under its dot)
// and alef, which ends the join; then feh (a loop under its dot) and teh marbuta (a loop under two dots)
const SCRIPT = [
  '..........1.......11',
  '.1.1..1...1.....11..',
  '..........1.1..1....',
  '..11.111..1.....1...',
  '.1.1.1.1..1.1....1..',
  '11111111..1111111111',
];
function renderAd(line) {
  const p = painter(BB.w, BB.h);
  for (let y = 0; y < BB.h; y++) for (let i = 0; i < BB.w; i++) p.set(i, y, dpick(AD_BG, 0.1 + 0.8 * y / (BB.h - 1) + 0.12 * Math.abs(i - 30) / 30, i, y));
  for (let i = 0; i < BB.w; i++) { p.set(i, 0, '#e0a840'); p.set(i, BB.h - 1, '#e0a840'); }
  paintRows(p, TRAY_ROWS, TRAY_KEY, 1, 6);
  const mid = 32, row = (s, y, colAt) => ptext(p, s, mid - ((s.length * 4 - 1) >> 1), y, colAt);
  row('KONAFA', 3, () => '#200408'); row('KONAFA', 2, j => j < 2 ? '#fff0b0' : '#ffc84a');
  if (line) row(line, 9, () => '#f6ead8');
  else paintRows(p, SCRIPT, { 1: '#f6ead8' }, mid - (SCRIPT[0].length >> 1), 8);
  for (let i = 1, k = 0; i < BB.w - 2; i += 3, k++) { const col = ZEENA_COLS[k % 6]; p.set(i, 16, col); p.set(i + 1, 16, col); p.set(i, 17, col); }
  return p.done();
}
function drawOwnAd(t) {
  const q = t * 30 / LOOP + 0.3;                                                // the second line changes every 8 s
  ctx.drawImage(AD_C[Math.floor(q) & 1], BB.x, BB.y);
  ctx.fillStyle = '#ffffff';                                                    // steam off the warm konafa
  for (let k = 0; k < 3; k++) {
    const u = frac(t * (30 + k * 7) / LOOP + k * 0.37), y = BB.y + 6 - Math.floor(u * 5);
    const x = BB.x + 5 + k * 3 + Math.round(Math.sin(u * 6 + k * 2 + TAU * 11 * t / LOOP));
    ctx.globalAlpha = 0.65 * Math.sin(Math.PI * u); ctx.fillRect(x, y, 1, 1);
  }
  const g = t % 10;                                                             // syrup glints rolling over the tray
  if (g < 0.8) { ctx.globalAlpha = 0.9; ctx.fillStyle = '#fff8d0'; ctx.fillRect(BB.x + 4 + Math.floor(g / 0.8 * 10), BB.y + 8 + Math.floor(g / 0.8 * 2), 1, 1); }
  ctx.globalAlpha = 1;
}
function drawBillboard(t, pitch) {
  halo(HL.bbSky, BB.x + (BB.w >> 1), BB.y + (BB.h >> 1), 0.8);
  ctx.drawImage(BB_FR_C, BB_FR.x, BB_FR.y);
  if (!PITCH.covers(t, pitch)) drawOwnAd(t);
  PITCH.draw(ctx, t, BB.x, BB.y, pitch);
  ctx.globalCompositeOperation = 'lighter';                                     // the lamps wash the top of the face
  ctx.globalAlpha = 0.1;
  ctx.drawImage(BB_WASH_C, BB.x, BB.y);
  ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  for (const lx of BB_LAMPS) halo(HL.bbLamp, lx, BB.y - 5, 0.75 + 0.1 * wave(t, 900 + lx));
}
function renderWash() {
  const p = painter(BB.w, BB.h);
  for (let y = 0; y < 5; y++) for (let i = 0; i < BB.w; i++) {
    const v = Math.max(...BB_LAMPS.map(lx => 1 - Math.hypot((i + BB.x - lx) / 8, y / 5)));
    if (v > bayer(i, y) * 0.9) p.set(i, y, '#fff0c8');
  }
  return p.done();
}

/* ================= Our roof: a tent-fabric valance, the parapet, tiles, benches, the tray and the shisha ================= */
const KHAY = { r: '#b82a2a', R: '#8a1a22', b: '#2a4aa8', y: '#f0c040', g: '#2a8a4a', w: '#f0e6d2', k: '#3a0e14' };
// the printed Ramadan tent fabric (khayamiya print): an 8-px tile of diamonds and rings
const KHAY_TILE = ['kkkkkkkk', 'rryrrrry', 'rbbrryrr', 'bwybrrgr', 'rbbrryrr', 'rryrrrry', 'yyyyyyyy', 'RRRRRRRR'];
const fabricAt = (x, y) => KHAY[KHAY_TILE[((y % 8) + 8) % 8][((x % 8) + 8) % 8]];
const POOLS = [[FAN.x, 120, 110, 0.9], [SHISHA.x, 214, 60, 0.7], [220, 230, 90, 0.5], [420, 226, 80, 0.45]];
const poolAt = (i, y) => { let v = 0; for (const [x0, y0, rr, a] of POOLS) v = Math.max(v, a * Math.max(0, 1 - Math.hypot(i - x0, (y - y0) * 1.3) / rr)); return v; };
const TILES = ['#140c10', '#20141a', '#2e1c1e', '#402824', '#56362a', '#6e4632'];
function renderForeground() {
  const p = painter(W, H, true), r = rng(88), ledge = [];
  p.id = 0;
  // the parapet: a plastered ledge (a row of coloured bulbs on it, lit per frame), its inner face in shadow
  for (let x = 0; x < W; x++) {
    const L = poolAt(x, PAR + 4);
    p.set(x, PAR, dpick(['#5a4038', '#7a5846', '#9a7258', '#c0946c'], 0.3 + L * 0.8, x, PAR));
    p.set(x, PAR + 1, dpick(['#3a2a28', '#5a4034', '#7a5846'], 0.2 + L * 0.8, x, PAR + 1));
    for (let y = PAR + 2; y < FLOOR; y++) p.set(x, y, dpick(['#140c10', '#20141a', '#2e1e1e', '#402a24', '#56382c'], 0.12 + L * 0.8 - (y - PAR) / 50 + (hash(x >> 2, y >> 1, 3) - 0.5) * 0.08, x, y, 0.8));
    if (x % 5 === 2) ledge.push({ x, y: PAR - 1, k: ledge.length });
  }
  // the floor: big cement tiles in a simple two-tone pattern, warm where the lamps fall
  for (let y = FLOOR; y < H; y++) for (let x = 0; x < W; x++) {
    const L = poolAt(x, y), gy = Math.floor((y - FLOOR) / (5 + (y - FLOOR) * 0.12)), gx = Math.floor((x - 240) / (12 + (y - FLOOR) * 0.4) + 100);
    const grout = y === FLOOR;
    p.set(x, y, grout ? '#0e080c' : dpick(TILES, 0.14 + L * 0.9 + ((gx + gy) & 1 ? 0.06 : -0.04) - (y - FLOOR) / 160, x, y, 0.8));
  }
  // the valance of printed tent fabric along the top, pointed flaps hanging from it
  for (let y = 0; y < 8; y++) for (let x = 0; x < W; x++) p.set(x, y, y === 7 ? '#5a1418' : mixHex(fabricAt(x, y), '#1a0810', 0.2));
  for (let x = 0; x < W; x++) { const k = x % 12, d = k < 6 ? k : 11 - k; for (let y = 8; y < 8 + d; y++) p.set(x, y, y === 7 + d ? '#3a0e14' : mixHex(fabricAt(x, y), '#1a0810', 0.35)); }
  // the tied-back side panel of the same fabric on the left
  for (let y = 8; y < FLOOR + 30; y++) {
    const w = y < 150 ? 9 - Math.round(Math.max(0, (y - 110) / 40) * 4) : 5 + Math.round(Math.min(1, (y - 150) / 50) * 7);
    for (let x = 0; x < w; x++) p.set(x, y, mixHex(fabricAt(x, y), '#140810', x === w - 1 ? 0.6 : 0.3 + (x & 1) * 0.1));
  }
  for (let x = 0; x < 7; x++) { p.set(x, 149, '#e8c060'); p.set(x, 150, '#a88030'); }   // its tie-back cord
  // a kilim on the floor under the table, its near edge wider (it lies flat, toward us)
  for (let y = 227; y < 262; y++) {
    const f = (y - 227) / 35, x0 = Math.round(lerp(168, 150, f)), x1 = Math.round(lerp(318, 340, f));
    for (let x = x0; x <= x1; x++) {
      const u = (x - x0) / (x1 - x0), bord = y < 229 || y > 259 || u < 0.04 || u > 0.96, L = poolAt(x, y);
      const dx = Math.abs(((u * 9) % 1) - 0.5) * 2, dy = Math.abs((((y - 227) / 7) % 1) - 0.5) * 2, diamond = dx + dy < 0.9;
      const col = bord ? (((x + y) >> 1) & 1 ? '#e8b040' : '#2a1410') : diamond ? (dx + dy < 0.45 ? '#e8b040' : '#1a1a3a') : '#a82a22';
      p.set(x, y, mixHex(col, '#0e0608', clamp(0.66 - L * 0.6 + f * 0.2, 0, 0.85)));
    }
  }
  // benches of turned wood (a mashrabiya-style back), seen from behind: cushions show between the spindles, the floor
  // between the legs; bolsters poke up over the top rail
  const bench = (x0, x1, top, fx) => {
    const wood = (x, y, v) => dpick(['#1e1008', '#3a2210', '#5a3618', '#7e4e22', '#a06a30'], v + poolAt(x, y) * 0.9, x, y, 0.7);
    for (let x = x0; x < x1; x++) {
      p.set(x, top, wood(x, top, 0.5)); p.set(x, top + 1, wood(x, top + 1, 0.3));
      for (let y = top + 2; y <= top + 8; y++) {
        const sp = (x - x0) % 3 === 0;
        p.set(x, y, sp ? wood(x, y, y === top + 5 ? 0.55 : 0.32) : mixHex(fabricAt(x + fx, y), '#140810', 0.5 - poolAt(x, y) * 0.35));
      }
      p.set(x, top + 9, wood(x, top + 9, 0.45)); p.set(x, top + 10, wood(x, top + 10, 0.25));
      for (let y = top + 11; y <= top + 13; y++) p.set(x, y, wood(x, y, y === top + 11 ? 0.2 : 0.1));
    }
    for (const lx of [x0, x0 + 1, (x0 + x1) >> 1, x1 - 2, x1 - 1]) for (let y = top + 14; y <= top + 25; y++) p.set(lx, y, wood(lx, y, lx === x0 || lx === x1 - 1 ? 0.3 : 0.15));
    for (let x = x0; x < x1; x++) p.set(x, top + 25, '#0e0608');                 // its shadow on the floor
  };
  bench(14, 132, 210, 0); bench(372, 472, 212, 4);
  const bolster = (x0, x1, y0, col) => { for (let x = x0; x < x1; x++) for (let y = y0; y < y0 + 5; y++) {
    const e = Math.abs(y - y0 - 2) / 2.6 + ((x === x0 || x === x1 - 1) ? 0.5 : 0);
    if (e > 1.1) continue;
    p.set(x, y, y === y0 ? mixHex(col, '#fff0d0', 0.35) : (x + y) % 4 === 0 ? '#f0c040' : mixHex(col, '#140810', (y - y0) * 0.1));
  } };
  bolster(18, 38, 205, '#c83a3a'); bolster(72, 92, 205, '#2a8a4a'); bolster(440, 466, 207, '#2a5ab0');
  // two floor cushions by the table, and a pot of basil against the parapet
  for (const [x0, col] of [[178, '#2a5ab0'], [252, '#c83a3a']]) for (let y = 230; y < 240; y++) for (let x = x0; x < x0 + 16; x++) {
    const top = y < 233;
    p.set(x, y, top ? mixHex(fabricAt(x, y), '#fff0d0', y === 230 ? 0.2 : 0) : mixHex(col, '#0e0608', 0.4 + (y - 233) * 0.05 - poolAt(x, y) * 0.4));
  }
  for (let y = 226; y < 238; y++) { const hw = y < 228 ? 5 : 4 - ((y - 228) >> 2); for (let x = 146 - hw; x <= 146 + hw; x++) p.set(x, y, y === 226 ? '#c86a3a' : x < 146 ? '#a8522a' : '#7a3a1e'); }
  for (let k = 0; k < 70; k++) {
    const a = -Math.PI / 2 + (r() - 0.5) * 2.6, len = 3 + r() * 9, x = 146 + Math.cos(a) * len, y = 225 + Math.sin(a) * len * 0.9;
    p.set(x, y, r() < 0.3 ? '#4a8a3a' : r() < 0.6 ? '#2a5a2a' : '#1a3a1c');
  }
  // the low table: a brass tray on a folding wooden stand
  for (let y = 222; y < 240; y++) for (const x of [206, 212, 228, 234]) if (y > 226) p.set(x + (y > 232 ? (x < 220 ? -1 : 1) : 0), y, '#3a2416');
  for (let y = 221; y <= 227; y++) {
    const hw = [9, 13, 15, 16, 15, 13, 9][y - 221];
    for (let x = 220 - hw; x <= 220 + hw; x++) {
      const rim = y === 221 || y === 227 || Math.abs(x - 220) >= hw - 1;
      p.set(x, y, rim ? (y > 224 ? '#8a5a20' : '#f0c060') : dpick(['#8a5a20', '#b87a2c', '#e0a840', '#f8d070'], 0.35 + poolAt(x, y) * 0.8 + (((x - 220) * (x - 220) + (y - 224) * 9) % 23 < 3 ? 0.25 : 0), x, y, 0.7));
    }
  }
  for (const [x, y] of [[227, 223], [230, 224], [224, 225]]) p.set(x, y, '#5a2a16');   // dates on a saucer
  // the shisha: a coloured glass base, the brass stem, the bowl (its coals glow per frame), the hose to the right bench
  const S = SHISHA.x;
  [1, 1, 1, 2, 3, 4, 5, 5, 6, 6, 6, 6, 6, 5, 5, 4].forEach((hw, j) => { const y = 226 + j; for (let x = S - hw; x <= S + hw; x++) p.set(x, y, j < 3 ? '#c8a040' : x === S - hw + 1 ? '#8af0e0' : x > S + hw - 2 ? '#1a4a4a' : j > 11 ? '#2a6a6a' : ((x + y) & 3) ? '#2a8a86' : '#3aa8a0'); });   // the glass base
  for (let y = SHISHA.top + 5; y < 226; y++) { p.set(S, y, (y % 5) ? '#c8a040' : '#fff0a0'); p.set(S + 1, y, '#7a5a20'); if (y % 7 === 3) { p.set(S - 1, y, '#e0b850'); p.set(S + 2, y, '#8a6428'); } }
  for (let x = S - 4; x <= S + 5; x++) { p.set(x, SHISHA.top + 4, '#8a6428'); p.set(x, SHISHA.top + 5, '#5a3e1a'); }   // the tray under the bowl
  for (let y = SHISHA.top; y <= SHISHA.top + 3; y++) for (let x = S - 1; x <= S + 2; x++) p.set(x, y, '#4a2a18');   // the clay bowl
  for (let k = 0; k <= 60; k++) { const f = k / 60, x = S + 2 + f * 86, y = 230 + Math.sin(f * Math.PI) * 10 - f * 14; p.set(x, y, '#3a2a30'); p.set(x, y + 1, '#1a1218'); }
  return { c: p.done(), ledge };
}

/* ================= Sprites: the lane's traffic and people, the cat, the pigeons, the lanterns (all facing right) ================= */
const TUKTUK = [
  '....rrrrrrrrrrrrrr....',
  '...rRRRRRRRRRRRRRRr...',
  '...y.y.y.y.y.y.y.yr...',
  '...p......p...d.ggp...',
  '...p..ss..p..dddggp...',
  '...p..ss..p..dddgggp..',
  '..bbbbbbbbbbbbbbbbbbl.',
  '..bBBBBwBBBBBBBBBBBBL.',
  '..bBBBBBBBBBBBBBBBBb..',
  '..bbkkkbbbbbbbbbkkkb..',
  '...kKKKk......kKKKk...',
  '....kkk........kkk....',
];
const TUK_KEY = { r: '#7a1a2a', R: '#b8283a', y: '#f0c040', p: '#2a2a30', d: '#1a1418', g: '#4a6a88', s: '#3a2a34',
  b: '#1e3a6a', B: '#2e5aa0', w: '#f0e6d2', l: '#fff4c0', L: '#ffd870', k: '#101014', K: '#4a4a52' };
/** a horse (or with rider false/true), 4 walking frames: legs swing, head nods */
function makeHorse(frame, coat = ['#3a2418', '#5a3a24', '#7a5234'], plume = null, trot = false) {
  const p = painter(16, 14), [d, m, l] = coat, nod = frame & 1;
  for (let y = 5; y <= 9; y++) for (let x = 2; x <= 12; x++) {
    const e = ((x - 7) / 5.6) ** 2 + ((y - 7) / 2.6) ** 2;
    if (e <= 1) p.set(x, y, y === 5 ? l : y >= 9 ? d : m);
  }
  for (let k = 0; k < 5; k++) { p.set(11 + (k >> 1), 5 - k + nod, m); p.set(12 + (k >> 1), 5 - k + nod, k < 2 ? d : m); }   // neck
  p.set(13, 1 + nod, m); p.set(14, 1 + nod, m); p.set(14, 2 + nod, m); p.set(15, 2 + nod, d); p.set(13, 0 + nod, d);      // head, ear
  p.set(1, 6, d); p.set(0, 7, d); p.set(0, 8, d); p.set(1, 7, d);          // tail
  if (plume) { p.set(13, nod - 1 < 0 ? 0 : nod - 1, plume); p.set(12, 0, plume); }
  const sw = [[1, -1], [0, 0], [-1, 1], [0, 0]][frame], lift = trot ? [[1, 0], [0, 0], [0, 1], [0, 0]][frame] : [[0, 0], [0, 0], [0, 0], [0, 0]][frame];
  for (const [x, s, li] of [[4, sw[1], lift[1]], [5, sw[0], lift[0]], [10, sw[0], lift[0]], [11, sw[1], lift[1]]])
    for (let y = 10; y <= 13 - li; y++) p.set(x + Math.round(s * (y - 9) / 4), y, x === 5 || x === 11 ? d : m);
  return p.done();
}
function makeCaleche(frame) {                                             // a Giza carriage: hood, big wheels, the driver
  const p = painter(28, 18), wheel = (cx, cy, rr) => {
    for (let y = cy - rr; y <= cy + rr; y++) for (let x = cx - rr; x <= cx + rr; x++) {
      const dd = Math.hypot(x - cx, y - cy);
      if (dd > rr + 0.3) continue;
      if (dd > rr - 0.8) p.set(x, y, '#141014');
      else if (dd < 1) p.set(x, y, '#c8a040');
      else if (Math.abs(Math.sin(Math.atan2(y - cy, x - cx) * 3 + frame * 0.52)) < 0.22) p.set(x, y, '#e0b040');
    }
  };
  for (let y = 3; y <= 10; y++) for (let x = 2; x <= 11; x++) if (x - 2 < (y - 3) * 1.6 + 3) p.set(x, y, y === 3 ? '#3a3440' : x === 2 ? '#2a2430' : '#141218');   // the hood
  for (let y = 10; y <= 12; y++) for (let x = 2; x <= 22; x++) p.set(x, y, y === 10 ? '#5a2a2a' : y === 11 ? '#2a1418' : '#1a0e10');   // the body
  for (let x = 12; x <= 20; x++) p.set(x, 9, '#8a3a3a');                   // a red seat cushion
  paintRows(p, ['.hh.', 'hffh', '.ww.', 'wwww', 'wwww', '.ww.'], { h: '#e8e0d0', f: '#8a5a40', w: '#d8d0c4' }, 16, 3);   // the driver in a white galabeya
  for (let k = 0; k < 7; k++) p.set(20 + k, 5 - Math.round(k * 0.7), '#6a5a4a');   // the whip
  for (let x = 20; x <= 27; x++) p.set(x, 11, '#3a2a20');                  // the shafts
  wheel(6, 13, 4); wheel(19, 14, 3);
  for (let x = 22; x <= 27; x++) p.set(x, 8 + ((x & 1) ? 1 : 0), '#f0c040');   // brass fittings
  return p.done();
}
function makeCamel(frame, rider) {
  const p = painter(22, 22), bob = frame & 1;
  const coat = ['#6a4a2a', '#8a6438', '#a8804a'];
  for (let y = 7; y <= 12; y++) for (let x = 3; x <= 14; x++) {
    const e = ((x - 8.5) / 6) ** 2 + ((y - 10) / 2.8) ** 2, hump = ((x - 8) / 3.6) ** 2 + ((y - 7.5) / 2.6) ** 2;
    if (e <= 1 || hump <= 1) p.set(x, y + bob, y <= 7 ? coat[2] : y >= 12 ? coat[0] : coat[1]);
  }
  for (let k = 0; k < 7; k++) { const x = 14 + Math.round(k * 0.7), y = 10 - k + bob; p.set(x, y, coat[1]); p.set(x + 1, y, k > 3 ? coat[1] : coat[0]); }   // the neck
  for (const [x, y] of [[18, 3], [19, 3], [20, 3], [19, 2], [21, 4]]) p.set(x, y + bob, coat[1]);   // the head
  p.set(18, 2 + bob, coat[0]);
  if (rider) {                                                             // the handler riding home, blanket and saddle
    for (let x = 5; x <= 12; x++) { p.set(x, 5 + bob, '#b83a2a'); p.set(x, 6 + bob, x & 1 ? '#f0c040' : '#b83a2a'); }
    paintRows(p, ['.tt.', 'tffh', '.gg.', 'gggg', 'gggg'], { t: '#e8e4dc', f: '#7a5038', h: '#e8e4dc', g: '#4a5a7a' }, 7, 0 + bob);
  }
  const sw = [[2, -2], [1, -1], [-2, 2], [-1, 1]][frame];
  for (const [x, s] of [[5, sw[1]], [6, sw[0]], [12, sw[0]], [13, sw[1]]])
    for (let y = 13; y <= 21; y++) p.set(x + Math.round(s * (y - 12) / 9), y, y === 21 ? '#3a2818' : (x === 6 || x === 13) ? coat[0] : coat[1]);
  return p.done();
}
/** a pedestrian, 2 frames: galabeya or abaya, a scarf or a cap */
function makePerson(frame, { body, head, skin = '#8a5a40', h = 9, kid = false }) {
  const p = painter(4, h + 1);
  p.set(1, 0, head); p.set(2, 0, head); p.set(1, 1, kid ? head : skin); p.set(2, 1, head);
  for (let y = 2; y < h - 1; y++) { p.set(1, y, body); p.set(2, y, body); if (y > 2 && y < h - 3) p.set(0, y, mixHex(body, '#000000', 0.3)); }
  for (let y = h - 1; y <= h; y++) { p.set(frame ? 0 : 1, y, '#1a1418'); p.set(frame ? 3 : 2, y, '#1a1418'); }
  return p.done();
}
const CAT_ROWS = [                                                         // a ginger tabby on the parapet, from behind, watching the show
  ['.e.....e.', '.ee...ee.', '.ehhhhhe.', '.hhHhHhh.', 'hhhhhhhhh', '.hhhhhhh.', '..bbbbb..', '.bbBbbBb.', 'bbbbbbbbb', 'bBbbBbbBb', 'bbbbbbbbb', 'bbbbbbbbb', '.bbbbbbb.'],
  ['..e.....e', '..ee...ee', '..ehhhhhe', '..hhHhHhh', '.hhhhhhhh', '..hhhhhhh', '..bbbbb..', '.bbBbbBb.', 'bbbbbbbbb', 'bBbbBbbBb', 'bbbbbbbbb', 'bbbbbbbbb', '.bbbbbbb.'],
];
const CAT_KEY = { e: '#7a4420', h: '#b86a30', H: '#8a4a20', b: '#a85e2a', B: '#7a4020' };
/** a sprite rim-lit where its outline meets the air (the show's glow behind it) */
function rimLit(rows, key, rimCol, amt = 0.45) {
  const p = painter(rows[0].length, rows.length), has = (i, j) => rows[j]?.[i] && rows[j][i] !== '.';
  rows.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (has(i, j)) {
    let col = key[row[i]];
    if (!has(i - 1, j) || !has(i + 1, j) || !has(i, j - 1)) col = mixHex(col, rimCol, amt);
    p.set(i, j, col);
  } });
  return p.done();
}
/** the big hanging fanous: a brass dome and crown, a body of coloured glass behind pierced brass, a glass cone below */
function renderBigFanous() {
  const w = 17, c = 8, p = painter(w, 36);
  const B = ['#5a3a14', '#8a5e20', '#c08a30', '#f0c058'], glass = ['#ff5a3a', '#ffd050', '#5ae07a', '#4aa8ff'];
  const brass = (x, y, v) => p.set(c + x, y, B[clamp(Math.round(v), 0, 3)]);
  for (const [x, y] of [[-1, 0], [0, 0], [1, 0], [-1, 1], [1, 1], [0, 2]]) brass(x, y, 2);   // hanging ring
  [1, 2, 3, 4, 5].forEach((hw, j) => { for (let x = -hw; x <= hw; x++) brass(x, 3 + j, x < 0 ? 3 : 1.4); });   // the dome
  for (let x = -7; x <= 7; x++) { brass(x, 8, 1); brass(x, 9, (x & 1) ? 0 : 3); brass(x, 10, 1); }   // crown band (lit through its holes per frame)
  for (let x = -8; x <= 8; x++) { brass(x, 11, 2); brass(x, 12, 1); }
  for (let y = 13; y <= 25; y++) for (let x = -8; x <= 8; x++) {           // the glass body: three panels behind a pierced lattice
    const a = Math.abs(x), panel = x < -4 ? 0 : x > 4 ? 2 : 1, frame = a === 8 || a === 4 || y === 13 || y === 19 || y === 25;
    if (frame) brass(x, y, a === 8 ? 1 : 2);
    else if ((x * 3 + y * 5) % 7 === 0 || (x + y * 2) % 5 === 0) brass(x, y, 0.6);
    else p.set(c + x, y, mixHex(glass[panel], '#ffffff', (y === 14 || y === 20) ? 0.3 : 0));
  }
  for (let x = -8; x <= 8; x++) { brass(x, 26, 2); brass(x, 27, 1); }
  [7, 6, 5, 4, 3, 2].forEach((hw, j) => { for (let x = -hw; x <= hw; x++) { const y = 28 + j; if (Math.abs(x) === hw || (x + y) % 3 === 0) brass(x, y, 1.5); else p.set(c + x, y, glass[(x < 0 ? 3 : 0)]); } });
  brass(0, 34, 2); brass(0, 35, 1);
  return p.done();
}

/* ================= Timings (loop seconds) and particle tables ================= */
const TUKS = [{ a: 14, b: 25, dir: 1 }, { a: 132, b: 142, dir: -1 }, { a: 194, b: 205, dir: 1 }];   // a tuk-tuk nipping through
const CALECHE = { a: 50, b: 92, dir: -1 };                 // a carriage coming home from the plateau, right to left
const CAMELS = { a: 98, b: 172, dir: 1 };                  // three camels and their handler walking back to the stables
const HORSES = { a: 210, b: 236, dir: -1 };                // two horses, a boy riding one
const SPOT_BANDS = [{ a: 36, b: 56, x0: 118, x1: 256 }, { a: 125, b: 147, x0: 292, x1: 482 }, { a: 155, b: 177, x0: 44, x1: 256 }];
const METEOR = { a: 47, b: 47.7, x0: 330, y0: 16, x1: 282, y1: 36 };
const FLOCK_HOME = [168, 176, 224, 232];                   // the pigeons fly down to the loft, sit, then take off again
const PUFFS = 15;                                          // the smoker's exhales per loop
const CAT_TURN = [[38, 44], [121, 126], [197, 203]];       // the cat looks round at us
const CAT_EARS = [22.4, 67.9, 101.3, 158.6, 188.2, 229.1];
const DRAWS = [[10, 13], [58, 61], [102, 105], [150, 153], [214, 217]];   // the smoker draws on the shisha: coals brighten
const PEOPLE = [];                                         // strollers on the lane: [n cycles per loop, phase, dir, look]
{
  const r = rng(5150), LOOKS = [
    { body: '#e8e4dc', head: '#e8e4dc' }, { body: '#2a2a3a', head: '#1a1a24' }, { body: '#6a4a3a', head: '#3a2a20' },
    { body: '#1a1418', head: '#8a3a5a' }, { body: '#3a4a6a', head: '#1e1a1a' }, { body: '#1a1418', head: '#2a4a7a' },
    { body: '#c8b89a', head: '#e8e4dc' }, { body: '#e8c040', head: '#3a2a20', h: 6, kid: true, lantern: '#ff5a4a' },
    { body: '#5ab0e0', head: '#2a1a14', h: 6, kid: true, lantern: '#5ae07a' }, { body: '#e87aa0', head: '#1a1418', h: 6, kid: true, lantern: '#ffd04a' },
  ];
  LOOKS.forEach((look, i) => PEOPLE.push({ id: i, n: 3 + Math.floor(r() * 4), ph: r(), dir: r() < 0.5 ? 1 : -1, look, y: LANE - 1 - Math.floor(r() * 4) }));
}
const FALL = [[], [], []];                                 // zeena scraps torn loose: far, mid, near
{
  const r = rng(1357);
  [[16, 5, 8, 1, 22], [12, 8, 11, 2, 38], [8, 11, 14, 3, 64]].forEach(([count, nMin, nMax, size, drift], depth) => {
    for (let i = 0; i < count; i++) FALL[depth].push({
      id: depth * 100 + i, n: nMin + Math.floor(r() * (nMax - nMin + 1)), ph: r(), ks: 40 + Math.floor(r() * 40), p2: r(),
      kf: 30 + Math.floor(r() * 40), p4: r(), amp: (depth + 1) * 2.2 * (0.7 + r() * 0.6), drift: drift * (0.6 + r() * 0.8), size,
      col: ZEENA_COLS[Math.floor(r() * ZEENA_COLS.length)],
    });
  });
}
const FLOCK = [];
{
  const r = rng(808);
  for (let i = 0; i < 13; i++) FLOCK.push({ id: i, ph: i / 13 + r() * 0.04, rx: 30 + r() * 12, ry: 10 + r() * 5, dy: (r() - 0.5) * 8, flap: 1200 + Math.floor(r() * 300), perch: DOVE.x0 - 1 + Math.round(i * 2.1) });
}
const STEAM = [];
{
  const r = rng(61);
  for (let k = 0; k < 10; k++) STEAM.push({ x: FUL.x + 6 + Math.floor(r() * 6), n: 40 + Math.floor(r() * 16), ph: r(), k: 18 + Math.floor(r() * 20), p: r(), id: k, h: 8 + r() * 3 });
}
const TV = ['#4a6aff', '#8aa8ff', '#c8d8ff', '#6a60d0', '#a0c0ff'];
const BULB_COLS = ['#ff5a5a', '#ffd84a', '#5aff8a', '#5ab0ff', '#ff8ad8'];

// a picked weather (site/js/engine/weather.js): Cairo's own zeena scraps show only with its own weather. Far drops end
// on the rooftops across the lane and catch the lit monuments (and the sign's lamps), mid ones land in the lane under its
// lanterns and shop lights, near ones fall past our roof through the big fanous and the shisha's glow.
const WEATHER = weatherPlans(['zeena']);
const WX_LAND = [[140, 184], [186, 203], [H + 4, H + 4]];
const WX_FAR = [{ x: KF.x, y: 100, rx: 60, ry: 38, col: '#ffd890' }, { x: 380, y: 96, rx: 80, ry: 40, col: '#ffd890' },
  { x: SX, y: 132, rx: 22, ry: 18, col: '#ffe0a0' }, { x: MIN.x, y: 100, rx: 8, ry: 40, col: '#8affb0' }];
const WX_MID = [{ x: 63, y: 188, rx: 30, ry: 10, col: '#ffc070' }, { x: 122, y: 188, rx: 30, ry: 8, col: '#ffe0a0' },
  { x: 382, y: 188, rx: 30, ry: 8, col: '#e0fff0' }, { x: 200, y: 180, rx: 60, ry: 8, col: '#ffb070' }, { x: 320, y: 180, rx: 40, ry: 6, col: '#ffb070' }];
const WX_NEAR = [{ x: FAN.x, y: 78, rx: 26, ry: 30, col: '#ffc070' }, { x: SHISHA.x, y: SHISHA.top, rx: 16, ry: 14, col: '#ff8a4a' },
  { x: 240, y: 200, rx: 240, ry: 6, col: '#ffd8a0' }];
const WX_LEAVES = { wind: [0, 14, W, 196] };          // a rooftop at the desert's edge has no tree over it: the wind carries the leaves past
const wxDepths = far => [far, WX_MID, WX_NEAR].map(lights => ({ land: WX_LAND, lights, leaves: WX_LEAVES }));
const WX = wxDepths(WX_FAR);
const WX_BB = wxDepths([...WX_FAR, { x: BB.x + (BB.w >> 1), y: BB.y + 4, rx: 30, ry: 14, col: '#fff0c8' }]);   // + the sign's lamps

/* ================= Pre-render everything static ================= */
let HL, SKY_C, MOON_C, CLOUDS, DESERT_C, PLATEAU_C, MON, VIL, LANTERNS, MINA, STRANDS_C, FG, VIG_C, BB_FR_C, BB_WASH_C, AD_C, PITCH, SPR, BEAM_C;
function renderVignette() {
  const [c, x] = makeCanvas(W, H); const img = x.createImageData(W, H), d = img.data;
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    const v = smooth(0.8, 1.5, Math.hypot((i - W / 2) / (W / 2), (y - H / 2) / (H / 2) * 0.9));
    const q = Math.floor(v * 4 + bayer(i, y)) / 4;
    if (q <= 0) continue;
    const o = (y * W + i) * 4; d[o] = 8; d[o + 1] = 3; d[o + 2] = 14; d[o + 3] = Math.round(q * 0.4 * 255);
  }
  x.putImageData(img, 0, 0);
  return c;
}
/** the spotlight's soft cone from the lighting tower up to the Sphinx's face (drawn additively while it "speaks") */
function renderBeam() {
  const p = painter(W, H), ax = 242, ay = 139, bx = SX, by = STOP + 6;
  for (let y = STOP - 4; y < 146; y++) for (let x = SX - 16; x < 248; x++) {
    const L = Math.hypot(bx - ax, by - ay), u = ((x - ax) * (bx - ax) + (y - ay) * (by - ay)) / (L * L);
    if (u < 0 || u > 1.05) continue;
    const px0 = ax + u * (bx - ax), py0 = ay + u * (by - ay), dd = Math.hypot(x - px0, y - py0), rr = 1 + u * 9;
    if (dd > rr) continue;
    const v = (1 - dd / rr) * (0.35 + 0.65 * u);
    if (v > bayer(x, y) * 1.1) p.set(x, y, '#fff2d0');
  }
  return p.done();
}
function prerender() {
  const glow = n => SHOW[n].ramp[5];
  HL = {
    moon: makeHalo(30, '#a8a8e0', 0.12, 1.6),
    monA: {}, monU: {}, monM: {}, monS: {},
    lamp: makeHalo(5, '#ffd890', 0.4, 1.2), shop: makeHalo(16, '#ffc070', 0.16, 1.5), shopCool: makeHalo(16, '#d0fff0', 0.14, 1.5),
    lantern: FANOUS_COLS.map(c => makeHalo(5, c, 0.5, 1.2)), stall: makeHalo(24, '#ffa050', 0.2, 1.5),
    green: makeHalo(7, '#5aff8a', 0.35, 1.3), greenBig: makeHalo(26, '#3ad06a', 0.12, 1.5), bulb: makeHalo(3, '#fff0c0', 0.5, 1),
    fanous: makeHalo(40, '#ffb060', 0.2, 1.5), fanousCore: makeHalo(12, '#ffd890', 0.4, 1.3),
    coal: makeHalo(8, '#ff6a2a', 0.55, 1.2), cart: makeHalo(20, '#ffd08a', 0.22, 1.5), cartBulb: makeHalo(5, '#fff0c0', 0.6, 1.2),
    tuk: makeHalo(10, '#fff0c0', 0.45, 1.3), tail: makeHalo(3, '#ff3a3a', 0.6, 1), flood: makeHalo(5, '#fff0d0', 0.5, 1.2),
    flash: makeHalo(4, '#ffffff', 0.7, 1), bbSky: makeHalo(34, '#ffe0a0', 0.1, 1.5), bbLamp: makeHalo(5, '#fff0c8', 0.55, 1.2),
    tv: makeHalo(4, '#8aa8ff', 0.4, 1.2), crescent: makeHalo(12, '#fff0a0', 0.3, 1.4), kid: FANOUS_COLS.map(c => makeHalo(4, c, 0.55, 1.2)),
  };
  for (const n of new Set(PHASES.map(p => p.A))) HL.monA[n] = makeHalo(84, glow(n), 0.13, 1.6);
  for (const n of new Set(PHASES.map(p => p.U))) HL.monU[n] = makeHalo(92, glow(n), 0.13, 1.6);
  for (const n of new Set(PHASES.map(p => p.M))) HL.monM[n] = makeHalo(40, glow(n), 0.13, 1.6);
  for (const n of new Set(PHASES.map(p => p.S))) HL.monS[n] = makeHalo(26, glow(n), 0.2, 1.5);
  SKY_C = renderSky(); MOON_C = renderMoon(); CLOUDS = CLOUD_DEFS.map(makeCloudLayer);
  DESERT_C = renderDesert(); PLATEAU_C = renderPlateau();
  const maps = { M: buildMenkaure(), A: buildKhafre(), U: buildKhufu(), S: buildSphinx() };
  MON = {};
  for (const o of Object.keys(maps)) {
    const names = [...new Set(PHASES.map(p => p[o]))];
    MON[o] = { m: maps[o], c: Object.fromEntries(names.map(n => [n, paintMap(maps[o], n)])) };
  }
  VIL = renderVillage();
  // shop interiors and the loft go straight into the village canvas
  { const [c, x] = makeCanvas(W, H); x.drawImage(VIL.c, 0, 0);
    const p = painter(W, H); renderStreet(p); LANTERNS = renderShops(p, VIL.shops); renderDovecote(p); x.drawImage(p.done(), 0, 0); VIL.c = c; }
  MINA = renderMinaret(); STRANDS_C = renderStrands();
  FG = renderForeground(); VIG_C = renderVignette(); BEAM_C = renderBeam();
  BB_FR_C = renderBillboardFrame(); BB_WASH_C = renderWash(); AD_C = ['QATAYEF', null].map(renderAd);
  PITCH = makePitch(BB.w, BB.h, { accent: '#f0e6d2' });
  const horse = [0, 1, 2, 3].map(f => makeHorse(f, undefined, '#e83a3a', true)), grey = [0, 1, 2, 3].map(f => makeHorse(f, ['#6a6470', '#9a94a0', '#c8c2cc']));
  const bay = [0, 1, 2, 3].map(f => makeHorse(f, ['#2a1810', '#4a2a18', '#6a4024']));
  SPR = {
    tuk: [sprite(TUKTUK, TUK_KEY), sprite(TUKTUK, TUK_KEY, true)],
    caleche: [0, 1, 2, 3].map(f => flipCanvas(makeCaleche(f))), horse: horse.map(flipCanvas),
    camel: [0, 1, 2, 3].map(f => makeCamel(f, true)), camelLed: [0, 1, 2, 3].map(f => makeCamel(f, false)),
    grey: grey.map(flipCanvas), bay: bay.map(flipCanvas),
    people: PEOPLE.map(pp => [0, 1].map(f => { const c = makePerson(f, pp.look); return pp.dir > 0 ? c : flipCanvas(c); })),
    handler: [0, 1].map(f => makePerson(f, { body: '#4a5a7a', head: '#e8e4dc' })),
    cat: CAT_ROWS.map(rows => rimLit(rows, CAT_KEY, '#ffd890')), fanous: renderBigFanous(), ful: makeFulCart(),
    folk: BENCH_FOLK.map(f => rimLit(f.rows, f.key, '#ffc070', 0.3)),
  };
}

/* ================= The lane itself, the site's railings, the ful cart and the people on our benches ================= */
function renderStreet(p) {
  // the site's boundary in the gap: a stone wall topped with iron railings, the paved plaza by the gate, two lamps
  for (let x = 154; x < 248; x++) {
    for (let y = 170; y < 178; y++) p.set(x, y, (x - 154) % 3 === 0 && y < 176 ? '#1a1620' : y === 170 ? '#3a3440' : null);   // railings
    for (let y = 178; y < 186; y++) p.set(x, y, dpick(['#2a2228', '#3a2e32', '#4e3e3a', '#6a5448'], 0.3 + (y === 178 ? 0.5 : 0) + facadeLight(x, y) * 0.5 + (((x >> 2) + (y >> 1)) & 1 ? 0.06 : 0), x, y, 0.7));
    for (let y = 186; y < LANE - 2; y++) p.set(x, y, dpick(['#241c22', '#302428', '#3e2e2e', '#56403a'], 0.25 + facadeLight(x, y) * 0.55 + (((x + y) % 7) === 0 ? 0.1 : 0), x, y, 0.8));
  }
  for (const x of [196, 197, 198, 199, 200]) for (let y = 176; y < 186; y++) p.set(x, y, '#141018');   // the gate
  for (const lx of [172, 226]) { for (let y = 166; y < LANE - 2; y++) p.set(lx, y, y < 168 ? '#8a8490' : '#2a2830'); p.set(lx - 1, 166, '#fff0c0'); p.set(lx + 1, 166, '#fff0c0'); p.set(lx, 165, '#fff8e0'); }
  // the street: dusty asphalt, the kerb, warm under the shops
  for (let x = 0; x < W; x++) {
    p.set(x, LANE - 3, dpick(['#3a3036', '#56484a'], 0.3 + facadeLight(x, 190) * 0.8, x, LANE - 3));
    for (let y = LANE - 2; y < PAR; y++) p.set(x, y, dpick(['#1a1418', '#261c20', '#34282a', '#46363a'], 0.25 + facadeLight(x, 188) * 0.6 - (y - LANE) / 20 + (hash(x, y, 77) - 0.5) * 0.14, x, y, 0.8));
  }
}
const FUL_ROWS = [
  '....oo..................p.',
  '...oOOo.................p.',
  '....oo.........gggggggg.p.',
  '..oOOOOo.......gwwbwwwg.p.',
  '.oOhOOOOo......gbwwbwbg.p.',
  'oOhOOOOOOo.....gwbwwwbg.p.',
  'oOOOOOOOOo.....gggggggg.p.',
  '.oOOOOOOo...............p.',
  'yyyyyyyyyyyyyyyyyyyyyyyyyy',
  'GGGGGGGGGGGGGGGGGGGGGGGGGG',
  'GyGGGGyGGGGGGGGGyGGGGGyGGG',
  'GGGGGGGGGGGGGGGGGGGGGGGGGG',
  '...kkk.............kkk....',
  '..kKkKk...........kKkKk...',
  '...kkk.............kkk....',
];
const FUL_KEY = { o: '#5a5a6a', O: '#b0b0c0', h: '#f4f4ff', g: '#8a8a98', w: '#e8d8b0', b: '#c89a58', p: '#3a3a44',
  y: '#f0c040', G: '#2a6a3a', k: '#141014', K: '#6a6a70' };
const makeFulCart = () => sprite(FUL_ROWS, FUL_KEY);
const FOLK_HIJAB = ['...hhh...', '..hHhhh..', '.hHhhhhh.', '.hHhhhhh.', '.hhhhhhh.', '.hhhhhhh.', '..hhhhh..', '.hhhhhhh.', 'hhhhhhhhh',
  'hhhhhhhhh', 'bbhhhhhbb', 'bbbbbbbbb', 'bBbbbbbbb', 'bBbbbbbbb', 'bbbbbbbbb', 'bbbbbbbbb', 'bbbbbbbbb'];
const FOLK_MAN = ['..hhhh...', '.hhhhhh..', '.hhhhhhh.', '.hhhhhhh.', '.hhhhhhh.', '..sssss..', '...sss...', '.wwwwwww.', 'wWwwwwwww',
  'wWwwwwwww', 'wWwwwwwww', 'wwwwwwwww', 'wwwwwwwww', 'wwwwwwwww', 'wwwwwwwww', 'wwwwwwwww', 'wwwwwwwww', 'wwwwwwwww'];
const FOLK_SMOKER = ['...ccc...', '..ccccc..', '..hhhhh..', '.shhhhhs.', '..hhhhh..', '...sss...', '.ggggggg.', 'gGggggggg',
  'gGggggggg', 'gGggggggg', 'ggggggggg', 'ggggggggg', 'ggggggggg', 'ggggggggg', 'ggggggggg', 'ggggggggg'];
const BENCH_FOLK = [
  { x: 98, y: 210 - FOLK_HIJAB.length, rows: FOLK_HIJAB, key: { h: '#2a5a6a', H: '#4a8a8a', b: '#5a2440', B: '#7a3a58' } },
  { x: 110, y: 210 - FOLK_MAN.length, rows: FOLK_MAN, key: { h: '#140e10', s: '#8a5a40', w: '#c8c0b4', W: '#e8e0d4' } },
  { x: 384, y: 212 - FOLK_SMOKER.length, rows: FOLK_SMOKER, key: { c: '#e8e4dc', h: '#1a1210', s: '#8a5a40', g: '#6a6a7a', G: '#8a8a9a' } },
];

const CRESCENT = ['..111.', '.11...', '11....', '11..1.', '11.111', '11..1.', '.11...', '..111.'];

/* ================= Per-frame: sky, the show, the plateau ================= */
function drawSky(t) {
  ctx.drawImage(SKY_C, 0, 0);
  for (const s of STARS) {
    const a = s.b * (0.55 + 0.45 * wave(t, s.k, s.ph));
    ctx.globalAlpha = a; ctx.fillStyle = s.col; ctx.fillRect(s.x, s.y, 1, 1);
    if (s.big) { ctx.globalAlpha = a * 0.35; ctx.fillRect(s.x - 1, s.y, 3, 1); ctx.fillRect(s.x, s.y - 1, 1, 3); }
  }
  if (t >= METEOR.a && t < METEOR.b) {                                         // a shooting star over the desert
    const u = (t - METEOR.a) / (METEOR.b - METEOR.a);
    for (let k = 0; k < 7; k++) {
      const f = u - k * 0.035;
      if (f < 0) continue;
      ctx.globalAlpha = (1 - k / 7) * Math.sin(Math.PI * u) * 0.9; ctx.fillStyle = k ? '#c8d0ff' : '#ffffff';
      ctx.fillRect(Math.round(lerp(METEOR.x0, METEOR.x1, f)), Math.round(lerp(METEOR.y0, METEOR.y1, f)), 1, 1);
    }
  }
  ctx.globalAlpha = 0.7;
  const cloud = L => { const off = Math.floor(frac(L.k * t / LOOP) * W); ctx.drawImage(L.c, off - W, L.y); ctx.drawImage(L.c, off, L.y); };
  cloud(CLOUDS[0]);
  ctx.globalAlpha = 1;
  halo(HL.moon, MOON.x, MOON.y + 2, 0.9);
  ctx.drawImage(MOON_C, MOON.x - MOON.r - 1, MOON.y - MOON.r - 1);
  ctx.globalAlpha = 0.55; cloud(CLOUDS[1]); ctx.globalAlpha = 1;
}
/** how far the running sweep has crossed [x0, x1] (1: done, or none running) */
const sweepF = (sw, x0, x1) => sw.x == null ? 1 : clamp(sw.dir > 0 ? (sw.x - x0) / (x1 - x0) : (x1 - sw.x) / (x1 - x0), 0, 1);
const GROUPS = [{ o: 'M', hl: 'monM', cx: MK.x - 20, cy: 124, a: 0.9 }, { o: 'A', hl: 'monA', cx: KF.x, cy: 104, a: 1 }, { o: 'U', hl: 'monU', cx: 390, cy: 100, a: 1 }];
function drawGlows(t, sw) {
  const br = 0.92 + 0.08 * wave(t, 8);
  for (const g of GROUPS) {
    const { m } = MON[g.o], cur = PHASES[sw.k][g.o], prev = PHASES[sw.from][g.o], f = sweepF(sw, m.x0, m.x0 + m.w);
    if (prev !== cur) halo(HL[g.hl][prev], g.cx, g.cy, g.a * br * (1 - f));
    halo(HL[g.hl][cur], g.cx, g.cy, g.a * br * (prev !== cur ? f : 1));
  }
}
function drawGroup(o, sw) {
  const { m, c } = MON[o], cur = PHASES[sw.k][o], prev = PHASES[sw.from][o];
  if (sw.x == null || prev === cur) { ctx.drawImage(c[cur], m.x0, m.y0); return; }
  const xe = clamp(Math.round(sw.x) - m.x0, 0, m.w), [L, R] = sw.dir > 0 ? [c[cur], c[prev]] : [c[prev], c[cur]];
  if (xe > 0) ctx.drawImage(L, 0, 0, xe, m.h, m.x0, m.y0, xe, m.h);
  if (xe < m.w) ctx.drawImage(R, xe, 0, m.w - xe, m.h, m.x0 + xe, m.y0, m.w - xe, m.h);
  const s0 = clamp(xe - 2, 0, m.w), s1 = clamp(xe + 2, 0, m.w);             // the bright seam of the sweeping lamps
  if (s1 > s0) {
    ctx.globalCompositeOperation = 'lighter'; ctx.globalAlpha = 0.6;
    ctx.drawImage(c[cur], s0, 0, s1 - s0, m.h, m.x0 + s0, m.y0, s1 - s0, m.h);
    ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  }
}
/** a search beam of white light walking across a face: the lit canvas added back over a narrow band */
function drawBands(t, sw) {
  ctx.globalCompositeOperation = 'lighter';
  for (const b of SPOT_BANDS) {
    if (t < b.a || t >= b.b) continue;
    const u = (t - b.a) / (b.b - b.a), bx = Math.round(lerp(b.x0, b.x1, u)), fade = Math.sin(Math.PI * u);
    for (const o of ['M', 'A', 'U']) {
      const { m, c } = MON[o];
      for (const [dx, w, a] of [[-5, 2, 0.14], [-3, 6, 0.3], [3, 2, 0.14]]) {
        const s0 = clamp(bx + dx - m.x0, 0, m.w), s1 = clamp(bx + dx + w - m.x0, 0, m.w);
        if (s1 <= s0) continue;
        ctx.globalAlpha = a * fade;
        ctx.drawImage(c[PHASES[sw.k][o]], s0, 0, s1 - s0, m.h, m.x0 + s0, m.y0, s1 - s0, m.h);
      }
    }
  }
  ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
}
const voice = t => (t >= SPEAKS[0] + 2 && t < SPEAKS[1] - 2) ? hash(7, step(t, 1680), 1) * (0.5 + 0.5 * Math.abs(wave(t, 90))) : 0;
const LAMPS_S = [[168, 152], [180, 153], [208, 153], [220, 152]], LAMPS_A = [[160, 136], [214, 136], [236, 136]];
function drawSphinx(t, sw) {
  const { m, c } = MON.S, cur = PHASES[sw.k].S, prev = PHASES[sw.from].S, f = sweepF(sw, SX - 20, SX + 20);
  halo(HL.monS[cur], SX, 132, prev !== cur ? f : 1);
  if (prev !== cur) halo(HL.monS[prev], SX, 132, 1 - f);
  ctx.drawImage(c[prev !== cur && f < 1 ? prev : cur], m.x0, m.y0);
  if (prev !== cur && f > 0 && f < 1) { ctx.globalAlpha = f; ctx.drawImage(c[cur], m.x0, m.y0); ctx.globalAlpha = 1; }
  const spk = env(t, SPEAKS[0], SPEAKS[1], 2.5);
  if (spk > 0) {                                                               // the spotlight finds its face, which "speaks"
    ctx.globalCompositeOperation = 'lighter';
    ctx.globalAlpha = spk * (0.24 + 0.12 * voice(t)); ctx.drawImage(BEAM_C, 0, 0);
    ctx.globalAlpha = spk * 0.2 * voice(t); ctx.drawImage(c.white, SX - 6 - m.x0, 0, 13, 16, SX - 6, m.y0, 13, 16);
    ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  }
  const colS = SHOW[cur].ramp[6], colA = SHOW[PHASES[sw.k].A].ramp[6];
  for (const [x, y] of LAMPS_S) { ctx.fillStyle = colS; ctx.fillRect(x, y, 1, 1); halo(HL.flood, x, y, 0.55); }
  for (const [x, y] of LAMPS_A) { ctx.fillStyle = colA; ctx.fillRect(x, y, 1, 1); halo(HL.flood, x, y, 0.4); }
  ctx.fillStyle = '#fff8e0'; ctx.fillRect(241, 136, 3, 1); halo(HL.flood, 242, 137, 0.7 + 0.3 * spk);   // the lighting tower's lamps
}
function drawAudience(t) {
  const s = step(t, 1200), busy = 0.7 + 1.5 * env(t, SPEAKS[0], SPEAKS[1], 2);
  for (let k = 0; k < 40; k++) {                                               // heads in the stands, a phone screen or two
    const row = k % 8, x = 162 - row * 2 + Math.floor(hash(k, 3) * (72 + row * 3)), y = 153 + row * 2;
    ctx.fillStyle = hash(k, 4) < 0.15 ? '#8aa0d0' : '#0e0a10'; ctx.fillRect(x, y, 1, 1);
  }
  for (let k = 0; k < 4; k++) {                                                // phone flashes
    if (hash(k, s, 61) > 0.3 * busy) continue;
    const row = Math.floor(hash(k, s, 62) * 8), x = 162 - row * 2 + Math.floor(hash(k, s, 63) * (72 + row * 3)), y = 153 + row * 2;
    ctx.fillStyle = '#ffffff'; ctx.fillRect(x, y - 1, 1, 1); halo(HL.flash, x, y - 1, 0.8);
  }
}

/* ================= Per-frame: the village, its lights, the loft's pigeons ================= */
function drawMinaret(t) {
  ctx.drawImage(MINA.st, MIN_BOX.x, MIN_BOX.y);
  const bad = hash(3, step(t, 720), 5) < 0.12;                                 // one tube on the blink
  ctx.globalAlpha = 0.86 + 0.14 * wave(t, 12);
  ctx.drawImage(MINA.gr, MIN_BOX.x, MIN_BOX.y);
  if (bad) { ctx.fillStyle = '#1e3a2a'; ctx.fillRect(MIN.x + 3, 120, 1, 14); }
  ctx.globalAlpha = 1;
  halo(HL.greenBig, MIN.x, 92, 0.9); halo(HL.greenBig, MIN.x, 128, 0.7);
  for (const y of [63, 79, 102]) halo(HL.green, MIN.x, y, 0.8 + 0.2 * wave(t, 30, y / 100));
}
function drawWindows(t) {
  for (const w of VIL.wins) if (hash(w.id, Math.floor(frac(t / LOOP + w.off) * w.n), 3) < w.p) {
    ctx.fillStyle = w.col; ctx.fillRect(w.x, w.y, 3, w.style === 4 ? 2 : 4);                 // lit, some half behind a curtain
    if (w.style === 3) { ctx.fillStyle = '#2a1e1a'; ctx.fillRect(w.x + 1, w.y, 1, 4); }
    else if (w.style === 4) { ctx.fillStyle = '#5a3a2a'; ctx.fillRect(w.x, w.y + 2, 3, 2); }
    else if (w.style === 0) { ctx.fillStyle = '#fff4d8'; ctx.fillRect(w.x + 1, w.y + 1, 1, 1); }
  }
  const s = step(t, 1440);
  for (const v of VIL.tvs) {                                                   // Ramadan's series flickering on the TVs
    ctx.fillStyle = '#1a1a3a'; ctx.fillRect(v.x, v.y, 3, 4);
    ctx.globalAlpha = 0.5 + 0.5 * hash(v.id, s, 9); ctx.fillStyle = TV[Math.floor(hash(v.id, s >> 1, 8) * TV.length)]; ctx.fillRect(v.x, v.y + 1, 3, 3);
    ctx.globalAlpha = 1;
  }
  const b = step(t, 480);
  for (const u of VIL.bulbs) if ((u.k + b) % 4 !== 0) { ctx.fillStyle = BULB_COLS[(u.k + (b >> 2)) % BULB_COLS.length]; ctx.fillRect(u.x, u.y, 1, 1); }
  // the neon crescent and star on the tall house: a Ramadan decoration
  const [cx, cy] = BLD[1].crescent, on = hash(5, step(t, 960), 2) > 0.06;
  ctx.fillStyle = on ? '#fff0a0' : '#8a7a40';
  CRESCENT.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] === '1') ctx.fillRect(cx - 3 + i, cy - 2 + j, 1, 1); });
  if (on) halo(HL.crescent, cx, cy + 2, 0.8);
}
function drawShops(t) {
  const s = step(t, 1440);
  for (const sh of VIL.shops) {
    const cx = (sh.x0 + sh.x1) >> 1;
    if (sh.kind === 'juice' || sh.kind === 'cafe') halo(HL.shopCool, cx, sh.y0 + 4, 0.9);
    else if (sh.kind !== 'stable' && sh.kind !== 'stable2') halo(HL.shop, cx, sh.y0 + 4, 0.9);
    else { ctx.fillStyle = '#fff0c0'; ctx.fillRect(sh.x0 + 1, sh.y0 - 3, 1, 1); halo(HL.lamp, sh.x0 + 1, sh.y0 - 3, 0.8); }
    if (sh.kind === 'cafe') { ctx.fillStyle = TV[Math.floor(hash(1, s >> 1, 8) * TV.length)]; ctx.fillRect(26, sh.y0 + 2, 6, 3); halo(HL.tv, 29, sh.y0 + 3, 0.6); }
  }
  halo(HL.stall, 63, 190, 0.9);
  for (const l of LANTERNS) {                                                  // the lantern stall's stock, candles flickering
    const f = 0.7 + 0.3 * hash(l.id, s, 11), ci = FANOUS_COLS.indexOf(l.col);
    ctx.fillStyle = '#6a4a20'; ctx.fillRect(l.x, l.y, 2, 1);
    ctx.fillStyle = l.col; ctx.globalAlpha = f; ctx.fillRect(l.x, l.y + 1, 2, l.big ? 4 : 2); ctx.globalAlpha = 1;
    ctx.fillStyle = '#fff4c0'; if (l.big) ctx.fillRect(l.x, l.y + 2, 1, 1);
    if (l.big || l.id % 3 === 0) halo(HL.lantern[ci], l.x + 1, l.y + 2, f * (l.big ? 0.9 : 0.6));
  }
}
function drawStrands(t, size) {
  const s = step(t, 960);
  STRANDS_C.forEach((st, i) => { if (st.size === size) ctx.drawImage(st.frames[(s + st.ph) & 3], st.x, st.y); });
  for (const [si, f] of STRAND_LANTERNS) {                                     // fanous hung from the strings, swaying a little
    const S = STRANDS[si];
    if (S[5] !== size) continue;
    const [x0, y0] = strandPt(S, f), x = Math.round(x0 + wave(t, 40 + si, f) * 0.6), y = Math.round(y0) + 1, big = size === 2;
    const ci = (si * 3 + Math.round(f * 10)) % FANOUS_COLS.length, fl = 0.75 + 0.25 * hash(si, s, 13);
    ctx.fillStyle = '#2a1e18'; ctx.fillRect(x, y, 1, big ? 3 : 1);
    const by = y + (big ? 3 : 1), bw = big ? 5 : 3, bh = big ? 7 : 4;
    ctx.fillStyle = '#c08a30'; ctx.fillRect(x - (bw >> 1), by, bw, 1); ctx.fillRect(x - (bw >> 1), by + bh - 1, bw, 1);
    ctx.fillStyle = FANOUS_COLS[ci]; ctx.globalAlpha = fl; ctx.fillRect(x - (bw >> 1), by + 1, bw, bh - 2); ctx.globalAlpha = 1;
    ctx.fillStyle = '#8a5e20'; ctx.fillRect(x, by + 1, 1, bh - 2);
    ctx.fillStyle = '#fff4c0'; ctx.fillRect(x, by + (bh >> 1), 1, 1);
    ctx.fillStyle = '#c08a30'; ctx.fillRect(x, by + bh, 1, 1);
    halo(HL.lantern[ci], x, by + (bh >> 1), fl * (big ? 1 : 0.8));
  }
}
function drawFlock(t) {
  const [d0, d1, u0, u1] = FLOCK_HOME, s = step(t, 1200);
  const home = t >= d0 && t < u1, land = smooth(d0, d1, t) * (1 - smooth(u0, u1, t));
  ctx.fillStyle = '#1a1620';
  for (const b of FLOCK) {
    const a = TAU * (20 * t / LOOP + b.ph), ox = 272 + Math.cos(a) * b.rx + Math.sin(a * 2 + b.id) * 3, oy = 104 + b.dy + Math.sin(a) * b.ry;
    const px0 = b.perch, py0 = DOVE.top - 1;
    const x = Math.round(lerp(ox, px0, land)), y = Math.round(lerp(oy, py0, land));
    const flying = land < 0.98, up = flying && hash(b.id, step(t, b.flap), 4) < 0.5;
    const lit = Math.abs(x - KF.x) < 60 || x > 300;                          // against a lit face they show dark, over the sky pale
    ctx.fillStyle = lit && y < BASE ? '#1a1420' : '#b8b0c0';
    if (flying) { ctx.fillRect(x, y, 1, 1); ctx.fillRect(x - 1, y - (up ? 1 : 0), 1, 1); ctx.fillRect(x + 1, y - (up ? 1 : 0), 1, 1); }
    else { ctx.fillStyle = '#8a8490'; ctx.fillRect(x, y, 2, 1); if (hash(b.id, s >> 3, 5) < 0.5) ctx.fillRect(x + 1, y - 1, 1, 1); }
  }
  // the keeper on the loft waves his flag while they fly, then stands with it furled
  const kx = DOVE.x0 + 6, ky = DOVE.top - 8, waving = !home || t < d0 + 3 || t >= u0;
  ctx.fillStyle = '#e8e4dc'; ctx.fillRect(kx, ky, 2, 1); ctx.fillStyle = '#8a5a40'; ctx.fillRect(kx, ky + 1, 2, 1);
  ctx.fillStyle = '#3a4a6a'; ctx.fillRect(kx, ky + 2, 2, 5); ctx.fillStyle = '#1a1418'; ctx.fillRect(kx, ky + 7, 1, 1); ctx.fillRect(kx + 1, ky + 7, 1, 1);
  const sw = waving ? wave(t, 80) : 0, tx = kx + 2 + Math.round(sw * 6), ty = ky - 10 + Math.round(Math.abs(sw) * 3);
  ctx.fillStyle = '#6a5a4a';
  for (let k = 0; k <= 10; k++) ctx.fillRect(Math.round(lerp(kx + 2, tx, k / 10)), Math.round(lerp(ky + 3, ty, k / 10)), 1, 1);
  ctx.fillStyle = '#e8403a'; ctx.fillRect(tx - (sw > 0 ? 0 : 2), ty, 3, 2);
  // the loft's own flag
  const fr = step(t, 1440) % 3;
  ctx.fillStyle = '#3ab85a'; ctx.fillRect(DOVE.x1, DOVE.top - 18, 3, 1); ctx.fillRect(DOVE.x1, DOVE.top - 17, fr === 1 ? 2 : 3, 1);
}

/* ================= Per-frame: the lane ================= */
function drawFul(t) {
  const x = FUL.x, y = LANE - 4 - SPR.ful.height + 1, s = step(t, 1800);
  // the vendor behind his cart, ladling ful from the pot into a bowl
  const lad = frac(t / 3.2) < 0.4, vx = x + 11, vy = y - 6;
  ctx.fillStyle = '#e8e4dc'; ctx.fillRect(vx, vy, 3, 1); ctx.fillStyle = '#8a5a40'; ctx.fillRect(vx, vy + 1, 3, 2);
  ctx.fillStyle = '#5a6a8a'; ctx.fillRect(vx - 1, vy + 3, 5, 8);
  ctx.fillStyle = '#8a8a98'; ctx.fillRect(lad ? vx - 5 : vx - 3, lad ? vy + 3 : vy + 5, 4, 1);
  halo(HL.cart, x + 12, y + 2, 0.8);
  ctx.drawImage(SPR.ful, x, y);
  ctx.fillStyle = '#fff8e0'; ctx.fillRect(x + 23, y - 1, 2, 2); halo(HL.cartBulb, x + 24, y, 0.9 + 0.1 * hash(2, s, 3));
  ctx.fillStyle = '#fffaf4';                                                   // steam off the pot
  for (const st of STEAM) {
    const u = frac(t * st.n / LOOP + st.ph), xx = Math.round(st.x - 6 + wave(t, st.k, st.p) * 1.4 * u + Math.sin(u * 7 + st.id) * u);
    ctx.globalAlpha = 0.7 * Math.min(1, u * 5) * (1 - u); ctx.fillRect(xx, Math.round(y - 1 - u * st.h), 1, 1);
  }
  ctx.globalAlpha = 1;
  // a customer waiting with a round of bread
  ctx.fillStyle = '#2a2a3a'; ctx.fillRect(x - 5, LANE - 12, 2, 8); ctx.fillStyle = '#1a1418'; ctx.fillRect(x - 5, LANE - 14, 2, 2);
  ctx.fillStyle = '#d8b070'; ctx.fillRect(x - 3, LANE - 9, 2, 2);
}
function walkX(t, a, b, dir, x0 = -60, x1 = W + 60) { const f = (t - a) / (b - a); return Math.round(dir > 0 ? lerp(x0, x1, f) : lerp(x1, x0, f)); }
function drawTraffic(t) {
  // strollers and children with their lanterns, walking both ways
  for (const p of PEOPLE) {
    const u = frac(p.n * t / LOOP + p.ph), x = Math.round(p.dir > 0 ? lerp(-10, W + 10, u) : lerp(W + 10, -10, u));
    const c = SPR.people[p.id][step(t, 1200 + p.id * 60) & 1];
    ctx.drawImage(c, x, p.y - c.height + 1);
    if (p.look.lantern) {
      const sw = Math.round(wave(t, 300 + p.id * 20) * 1.2), lx = x + (p.dir > 0 ? 3 : 0) + sw, ly = p.y - 4;
      ctx.fillStyle = p.look.lantern; ctx.fillRect(lx, ly, 1, 2);
      halo(HL.kid[FANOUS_COLS.indexOf(p.look.lantern)], lx, ly + 1, 0.8);
    }
  }
  // the camels coming home from the plateau, the first one ridden, their handler walking ahead
  if (t >= CAMELS.a && t < CAMELS.b) {
    const X = walkX(t, CAMELS.a, CAMELS.b, 1, -90, W + 30), f = step(t, 720) & 3;
    for (let k = 0; k < 3; k++) {
      const spr = (k === 0 ? SPR.camel : SPR.camelLed)[(f + k) & 3], x = X - k * 24;
      ctx.drawImage(spr, x, LANE - spr.height + 1);
      if (k) { ctx.fillStyle = '#2a2020'; for (let i = x + 20; i < x + 26; i++) ctx.fillRect(i, LANE - 18 + ((i - x) & 1), 1, 1); }   // the lead ropes
    }
    ctx.drawImage(SPR.handler[step(t, 1200) & 1], X + 26, LANE - 9);
  }
  // the carriage, a plume on its horse
  if (t >= CALECHE.a && t < CALECHE.b) {
    const x = walkX(t, CALECHE.a, CALECHE.b, -1, -70, W + 20), f = step(t, 1440) & 3;
    ctx.drawImage(SPR.horse[f], x, LANE - SPR.horse[f].height + 1);
    ctx.drawImage(SPR.caleche[f], x + 14, LANE - SPR.caleche[f].height + 1);
    ctx.fillStyle = '#fff0c0'; ctx.fillRect(x + 16, LANE - 10, 1, 1); halo(HL.lamp, x + 16, LANE - 10, 0.8);   // its carriage lamp
  }
  // two horses going home, a boy riding the grey
  if (t >= HORSES.a && t < HORSES.b) {
    const x = walkX(t, HORSES.a, HORSES.b, -1, -40, W + 20), f = step(t, 1200) & 3;
    ctx.drawImage(SPR.bay[f], x + 20, LANE - 13);
    ctx.drawImage(SPR.grey[(f + 2) & 3], x, LANE - 13);
    ctx.fillStyle = '#e8c040'; ctx.fillRect(x + 7, LANE - 18, 2, 4); ctx.fillStyle = '#1a1418'; ctx.fillRect(x + 7, LANE - 20, 2, 2);
  }
  // the tuk-tuks
  for (const T of TUKS) {
    if (t < T.a || t >= T.b) continue;
    const x = walkX(t, T.a, T.b, T.dir, -30, W + 8), spr = SPR.tuk[T.dir > 0 ? 0 : 1], y = LANE - spr.height + 1 + (step(t, 2400) & 1 ? 0 : -1) * (hash(3, step(t, 600)) < 0.3 ? 1 : 0);
    ctx.drawImage(spr, x, y);
    const hx = T.dir > 0 ? x + 21 : x, tx = T.dir > 0 ? x + 2 : x + 19;
    halo(HL.tuk, hx + T.dir * 3, y + 7, 0.9); halo(HL.tail, tx, y + 8, 0.8);
    ctx.fillStyle = '#ff3a3a'; ctx.fillRect(tx, y + 8, 1, 1);
    ctx.fillStyle = '#fff4c0'; ctx.globalAlpha = 0.14;                        // its headlight on the dust
    for (let k = 1; k < 18; k++) ctx.fillRect(hx + T.dir * k, y + 8 + (k >> 3), 1, 1 + (k >> 2));
    ctx.globalAlpha = 1;
  }
}

/* ================= Zeena scraps: torn paper pennants fluttering down on the breeze ================= */
function drawZeena(depth, t) {
  const land = depth === 0 ? [140, 182] : depth === 1 ? [188, 203] : [H + 8, H + 8];
  for (const z of FALL[depth]) {
    const q = t * z.n / LOOP + z.ph, cyc = Math.floor(q) % z.n, u = q - Math.floor(q), sw = wave(t, z.ks, z.p2);
    const end = lerp(land[0], land[1], hash(z.id, cyc, 7));
    const y = -6 + u * (end + 6) - Math.abs(sw) * (depth + 1) * 1.4;
    const x = -40 + hash(z.id, cyc, 5) * (W + 40) + u * z.drift + sw * z.amp;
    if (x < -4 || x > W + 4) continue;
    const fl = Math.cos(TAU * (z.kf * t / LOOP + z.p4)), back = fl < 0, edge = Math.abs(fl) < 0.3;
    ctx.globalAlpha = (depth < 2 && u > 0.9 ? (1 - u) / 0.1 : 1) * (depth === 0 ? 0.75 : 1);
    ctx.fillStyle = back ? mixHex(z.col, '#1a1020', 0.35) : z.col;
    const X = Math.round(x), Y = Math.round(y);
    if (z.size === 1) ctx.fillRect(X, Y, 1, 1);
    else if (z.size === 2) ctx.fillRect(X, Y, edge ? 1 : 2, edge ? 2 : 1 + (back ? 0 : 1) * 0);
    else if (edge) ctx.fillRect(X + 1, Y, 1, 3);
    else { ctx.fillRect(X, Y, 3, 1); ctx.fillRect(X + (sw > 0 ? 1 : 0), Y + 1, 2, 1); ctx.fillRect(X + 1, Y + 2, 1, 1); }
  }
  ctx.globalAlpha = 1;
}

/* ================= Per-frame: our roof ================= */
function drawRoof(t) {
  ctx.drawImage(FG.c, 0, 0);
  const b = step(t, 480);                                                      // the string of bulbs along our parapet
  for (const l of FG.ledge) {
    const on = (l.k + b) % 5 !== 0, ci = l.k % BULB_COLS.length;
    ctx.fillStyle = BULB_COLS[ci]; ctx.globalAlpha = on ? 1 : 0.35; ctx.fillRect(l.x, l.y, 1, 1); ctx.globalAlpha = 1;
    if (on && l.k % 4 === 0) halo(HL.bulb, l.x, l.y, 0.5);
  }
  // tea in little glasses on the tray, steam curling off them
  for (const [x, y] of [[212, 221], [217, 222], [223, 221]]) {
    ctx.fillStyle = '#e8f0f8'; ctx.fillRect(x, y - 3, 2, 1); ctx.fillStyle = '#c85a1a'; ctx.fillRect(x, y - 2, 2, 2); ctx.fillStyle = '#6ac04a'; ctx.fillRect(x + 1, y - 3, 1, 1);
  }
  ctx.fillStyle = '#ffffff';
  for (let k = 0; k < 5; k++) {
    const u = frac(t * (24 + k * 5) / LOOP + k * 0.21), x = [212, 217, 223, 213, 222][k] + Math.round(Math.sin(u * 5 + k + TAU * 9 * t / LOOP) * u * 1.5);
    ctx.globalAlpha = 0.45 * Math.sin(Math.PI * u); ctx.fillRect(x, Math.round(217 - u * 9), 1, 1);
  }
  ctx.globalAlpha = 1;
  // the shisha: coals glowing on the bowl, brighter as he draws on it; smoke from the coals and his exhales
  const draw = DRAWS.some(([a, c]) => t >= a && t < c) ? 1 : 0, s = step(t, 1800);
  for (let k = 0; k < 3; k++) { ctx.fillStyle = hash(k, s, 3) < 0.3 + draw * 0.4 ? '#ffb040' : '#e8481e'; ctx.fillRect(SHISHA.x - 1 + k, SHISHA.top - 1, 1, 1); }
  halo(HL.coal, SHISHA.x, SHISHA.top - 1, 0.6 + 0.3 * draw + 0.1 * hash(4, s, 2));
  for (let k = 0; k < 4; k++) {
    const u = frac(t * (20 + k * 3) / LOOP + k * 0.27), x = Math.round(SHISHA.x + Math.sin(u * 4 + k) * u * 2.5 + wave(t, 9 + k, k * 0.3) * u * 2);
    ctx.globalAlpha = 0.3 * (1 - u) * Math.min(1, u * 6); ctx.fillStyle = '#d8d0e0'; ctx.fillRect(x, Math.round(SHISHA.top - 3 - u * 22), 1, 1);
  }
  const pu = frac(t * PUFFS / LOOP);
  if (pu < 0.3) {                                                              // an exhale, drifting up and away
    const u = pu / 0.3, sx = 391, sy = 200;
    for (let k = 0; k < 9; k++) {
      const r = u * (2 + k * 0.6), a = k * 2.4;
      ctx.globalAlpha = 0.45 * (1 - u) * (k < 5 ? 1 : 0.6); ctx.fillStyle = '#e0dcea';
      ctx.fillRect(Math.round(sx - u * 10 + Math.cos(a) * r), Math.round(sy - u * 12 + Math.sin(a) * r * 0.6), 1, 1);
    }
  }
  ctx.globalAlpha = 1;
  // the people on our benches, from behind: a couple on the left chatting, the smoker on the right
  BENCH_FOLK.forEach((f, i) => {
    const nod = i === 1 && hash(i, step(t, 240), 3) < 0.4 ? 1 : 0;
    ctx.drawImage(SPR.folk[i], 0, 0, f.rows[0].length, f.rows.length - nod, f.x, f.y + nod, f.rows[0].length, f.rows.length - nod);
  });
  // the cat on the parapet, watching the show: ears twitch, now and then it looks round; its tail swishes
  const look = CAT_TURN.some(([a, c]) => t >= a && t < c) ? 1 : 0, cs = SPR.cat[look], cy = PAR - cs.height + 1;
  ctx.drawImage(cs, CAT.x, cy);
  if (CAT_EARS.some(e => t >= e && t < e + 0.25)) { ctx.fillStyle = CAT_KEY.h; ctx.fillRect(CAT.x + 1 + look, cy, 1, 1); }
  if (look) { ctx.fillStyle = '#c8e070'; ctx.fillRect(CAT.x + 4, cy + 3, 1, 1); }   // an eye catching the light
  const tail = wave(t, 30) * 2 + wave(t, 97, 0.3) * 0.8;
  ctx.fillStyle = CAT_KEY.b;
  for (let k = 0; k < 8; k++) ctx.fillRect(CAT.x + 5 + Math.round(tail * k / 8), PAR + 1 + k, 1, 1);
  // the big fanous hanging from the valance
  const fl = 0.8 + 0.2 * hash(9, step(t, 2400), 1), sway = Math.round(wave(t, 12) * 0.6);
  ctx.fillStyle = '#2a1e18'; ctx.fillRect(FAN.x, 10, 1, FAN.top - 10);
  halo(HL.fanous, FAN.x + sway, FAN.top + 20, fl);
  ctx.drawImage(SPR.fanous, FAN.x - 8 + sway, FAN.top);
  halo(HL.fanousCore, FAN.x + sway, FAN.top + 19, fl);
  ctx.fillStyle = '#fff4c0'; ctx.globalAlpha = fl; ctx.fillRect(FAN.x + sway - 1, FAN.top + 9, 3, 1); ctx.globalAlpha = 1;   // light through the crown
}

/* ================= Frame compositor (back to front) ================= */
// billboard: false leaves the sign out (the loop videos); weather: a WEATHERS pick (site/js/engine/weather.js), the city's
// own when left out
function renderFrame(t, { pitch, billboard = true, weather, reducedMotion = false } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather), wxc = billboard ? WX_BB : WX, own = !!wx.zeena, sw = sweepAt(t);
  drawSky(t);
  drawGlows(t, sw);
  ctx.drawImage(DESERT_C, 0, 126);
  for (const o of ['M', 'A', 'U']) drawGroup(o, sw);
  drawBands(t, sw);
  ctx.drawImage(PLATEAU_C, 0, PLAT_Y);
  drawSphinx(t, sw);
  drawAudience(t);
  drawMinaret(t);
  if (billboard) drawBillboard(t, pitch);                                      // on the tallest house's roof
  drawFlock(t);
  if (own) drawZeena(0, t);                                                    // far scraps pass behind the houses
  ctx.drawImage(VIL.c, 0, 0);
  drawWindows(t);
  drawShops(t);
  drawWeather(ctx, wx.shared, 0, t, wxc[0], reducedMotion);
  drawStrands(t, 0);
  drawFul(t);
  drawTraffic(t);
  drawStrands(t, 1);
  if (own) drawZeena(1, t);
  drawWeather(ctx, wx.shared, 1, t, wxc[1], reducedMotion);
  drawRoof(t);
  drawStrands(t, 2);
  if (own) drawZeena(2, t);
  drawWeather(ctx, wx.shared, 2, t, wxc[2], reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

export default {
  id: 'cairo',
  name: 'Cairo',
  tagline: 'Ramadan night by the pyramids',
  timeZone: 'Africa/Cairo',
  accent: '#f0e6d2',
  focusX: SX,
  landmark: [150, 220],                                // the Sphinx with Khafre's pyramid rising behind it
  landmarkTop: KF.tip,                                 // the row of Khafre's tip, which landscape crops keep
  billboard: BB,
  ambience: { crowd: 0.32, traffic: 0.22, wind: 0.12 },
  events: [
    ...[1, 2, 4, 5, 6].map(k => ({ t: k * SCENE_LEN + 0.4, type: 'bell', x: 240, gain: 0.16, pitch: 0.5 })),   // the show's far music swelling with each new scene
    { t: SPEAKS[0] + 0.5, type: 'bell', x: SX, gain: 0.26, pitch: 0.5 }, { t: SPEAKS[0] + 3, type: 'chime', x: SX, gain: 0.18 },
  ].sort((a, b) => a.t - b.t),
  create(c) {
    ctx = c;
    ctx.imageSmoothingEnabled = false;
    prerender();
    return { render: renderFrame };
  },
};
